import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../modules/inventory/components/card.tsx";
import { Button } from "../modules/inventory/components/button.tsx";
import { Skeleton } from "../modules/inventory/components/skeleton.tsx";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../modules/inventory/components/table.tsx";
import { Badge } from "../modules/inventory/components/badge.tsx";
import { toast } from "../modules/inventory/hooks/use-toast.ts";
import { useAuth } from "../hooks/useAuth";
import { useSettings } from "../hooks/useSettings";
import { ConfigurableSelect } from "../modules/inventory/components/configurable-select.tsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../modules/inventory/components/select.tsx";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../modules/inventory/components/dialog.tsx";
import { 
  Users, 
  ShoppingCart, 
  DollarSign,
  TrendingUp,
  TrendingDown,
  Package,
  Clock,
  CheckCircle,
  AlertCircle,
  Bell,
  Download,
  FileText,
  Plus,
  Edit,
  Eye,
  BarChart3,
  Network,
  Truck,
  Package2,
  AlertTriangle,
  RefreshCw,
  Zap,
  Calendar,
  LineChart,
  Target,
  MapPinIcon,
  Phone,
  Trash2,
  Play,
  Pause,
  Power
} from "lucide-react";
import inventoryService from "../modules/inventory/services/inventoryService.ts";
import productService from "../modules/inventory/services/productService.ts";
import { Toaster } from "../modules/inventory/components/toaster.tsx";
import { useConfigurationOptions } from "../modules/configuration/hooks/useConfiguration.ts";
import configurationService from "../modules/configuration/services/configurationService.ts";
import EditStockModal from "../modules/inventory/components/EditStockModal.tsx";
import ProductManagementSmartSearch from "../components/ProductManagementSmartSearch";

export default function ProductManagementPage() {
  const { user } = useAuth();
  const { settings } = useSettings();
  
  // Get responsive grid classes based on font size
  const getGridClasses = () => {
    const fontSize = settings?.display?.fontSize || 'medium';
    
    // For large and extra-large font sizes, use 2 columns max
    if (fontSize === 'large' || fontSize === 'extra-large') {
      return 'grid grid-cols-1 md:grid-cols-2 gap-6';
    }
    
    // For small and medium font sizes, use the original responsive grid
    return 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6';
  };

  // Get card height classes based on font size
  const getCardHeightClasses = () => {
    const fontSize = settings?.display?.fontSize || 'medium';
    
    // Debug logging
    console.log('Current font size:', fontSize);
    
    // For large and extra-large font sizes, increase card height
    if (fontSize === 'large') {
      console.log('Applying large font height: min-h-[320px]');
      return 'min-h-[320px]';
    } else if (fontSize === 'extra-large') {
      console.log('Applying extra-large font height: min-h-[380px]');
      return 'min-h-[380px] ';
    }
    
    // For small and medium font sizes, use default height
    console.log('Applying default height: min-h-[280px]');
    return 'min-h-[280px] h-[280px]';
  };
  
  // Load product categories from configuration
  const { options: categoryOptions, isLoading: categoriesLoading, refetch: refetchCategories } = useConfigurationOptions('product_categories');

  // Utility function to normalize category names to keys
  const normalizeCategoryNameToKey = (categoryName: string): string => {
    return categoryName.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
  };

  // Utility function to find category key by label
  const findCategoryKeyByLabel = (categoryLabel: string): string => {
    if (!categoryLabel || categoryOptions.length === 0) return "";
    
    const matchingCategory = categoryOptions.find(option => 
      option.label === categoryLabel || option.value === categoryLabel
    );
    
    if (matchingCategory) {
      return matchingCategory.key;
    }
    
    // Fallback to normalized key
    const fallbackKey = normalizeCategoryNameToKey(categoryLabel);
    console.warn(`Category "${categoryLabel}" not found in configuration options, using fallback key: "${fallbackKey}"`);
    return fallbackKey;
  };

  // Utility function to find category label by key
  const findCategoryLabelByKey = (categoryKey: string): string => {
    if (!categoryKey || categoryOptions.length === 0) return "";
    
    const matchingCategory = categoryOptions.find(option => option.key === categoryKey);
    
    if (matchingCategory) {
      return matchingCategory.label;
    }
    
    // Fallback to converting key back to label format
    const fallbackLabel = categoryKey.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
    console.warn(`Category key "${categoryKey}" not found in options, using fallback label: "${fallbackLabel}"`);
    return fallbackLabel;
  };

  // Utility function to validate category consistency
  const validateCategoryConsistency = (productCategory: string): { isValid: boolean; message?: string } => {
    if (!productCategory) {
      return { isValid: true }; // Empty category is valid
    }

    const matchingCategory = categoryOptions.find(option => 
      option.label === productCategory || option.value === productCategory
    );

    if (!matchingCategory) {
      return {
        isValid: false,
        message: `Category "${productCategory}" not found in configuration. Please create this category first or update the product to use an existing category.`
      };
    }

    return { isValid: true };
  };

  // Utility function to truncate long text with ellipsis
  const truncateText = (text: string, maxLength: number = 20): string => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength - 3) + "...";
  };

  // Utility function to check if text needs truncation
  const needsTruncation = (text: string, maxLength: number): boolean => {
    return Boolean(text && text.length > maxLength);
  };

  // Utility function to get responsive max length based on screen size
  const getResponsiveMaxLength = (baseLength: number = 20): number => {
    // Conservative approach for different screen sizes
    // Could be enhanced with actual screen size detection using useMediaQuery
    return baseLength;
  };

  // State for API data
  const [products, setProducts] = useState<any>(null);
  const [analytics, setAnalytics] = useState<any>(null);
  const [productsLoading, setProductsLoading] = useState(false);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [productsError, setProductsError] = useState<string | null>(null);
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);
  
  // Product management states
  const [isCreatingProduct, setIsCreatingProduct] = useState(false);
  const [isUpdatingProduct, setIsUpdatingProduct] = useState(false);
  const [isDeletingProduct, setIsDeletingProduct] = useState(false);
  const [isActivatingProduct, setIsActivatingProduct] = useState(false);
  const [isDeactivatingProduct, setIsDeactivatingProduct] = useState(false);
  
  // Category management states
  const [showAddCategoryDialog, setShowAddCategoryDialog] = useState(false);
  const [showEditCategoryDialog, setShowEditCategoryDialog] = useState(false);
  const [selectedCategoryForEdit, setSelectedCategoryForEdit] = useState<any>(null);
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  const [isUpdatingCategory, setIsUpdatingCategory] = useState(false);
  const [isDeletingCategory, setIsDeletingCategory] = useState(false);

  // Edit Stock modal states
  const [showEditStockModal, setShowEditStockModal] = useState(false);
  const [showInventoryDetailsModal, setShowInventoryDetailsModal] = useState(false);
  const [selectedInventoryItem, setSelectedInventoryItem] = useState<any>(null);
  const [selectedProductForStock, setSelectedProductForStock] = useState<any>(null);
  const [selectedProductInventory, setSelectedProductInventory] = useState<any>(null);
  const [isUpdatingStock, setIsUpdatingStock] = useState(false);

  // Inventory data states
  const [inventoryData, setInventoryData] = useState<any[]>([]);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [inventoryError, setInventoryError] = useState<string | null>(null);
  
  // Smart search states
  const [smartSearchTerm, setSmartSearchTerm] = useState("");
  const [smartSearchField, setSmartSearchField] = useState("all");

  // Inventory filters
  const [inventoryFilters, setInventoryFilters] = useState({
    category: 'All Categories',
    stockLevel: 'All Levels',
    urgentOnly: false
  });

  // Helper functions for inventory display
  const categories = useMemo(() => {
    const cats = new Set<string>();
    inventoryData.forEach(item => {
      if (item.category) cats.add(item.category);
    });
    const sortedCategories = Array.from(cats).sort();
    return ['All Categories', ...sortedCategories];
  }, [inventoryData]);

  const stockLevels = ['All Levels', 'Critical', 'Low', 'Sufficient', 'Over Stock'];

  const getStockLevel = (currentStock: number, minStock: number, maxStock: number): 'Critical' | 'Low' | 'Sufficient' | 'Over Stock' => {
    const percentage = maxStock > 0 ? (currentStock / maxStock) * 100 : 0;
    
    if (percentage < 15) return 'Critical';
    if (percentage < 40) return 'Low';
    if (percentage > 100) return 'Over Stock';
    return 'Sufficient';
  };

  // Calculate inventory statistics
  const inventoryStats = useMemo(() => {
    const stats = {
      totalItems: inventoryData.length,
      urgentItems: 0,
      criticalItems: 0,
      lowStockItems: 0,
      sufficientItems: 0,
      overStockItems: 0,
      totalValue: 0
    };

    inventoryData.forEach(item => {
      // Calculate overall product status based on total units vs total max capacity
      const totalUnits = item.totalStock || 0;
      const totalMaxCapacity = item.variants?.reduce((sum: number, variant: any) => sum + (variant.maxCapacity || 0), 0) || 100;
      const overallStockLevel = getStockLevel(totalUnits, 0, totalMaxCapacity);

      // Count items by status
      switch (overallStockLevel) {
        case 'Critical':
          stats.criticalItems++;
          stats.urgentItems++;
          break;
        case 'Low':
          stats.lowStockItems++;
          break;
        case 'Sufficient':
          stats.sufficientItems++;
          break;
        case 'Over Stock':
          stats.overStockItems++;
          break;
      }

      // Calculate total value
      if (item.totalValue) {
        stats.totalValue += item.totalValue;
      }
    });

    return stats;
  }, [inventoryData]);

  // Filter inventory data based on current filters
  const filteredInventory = useMemo(() => {
    return inventoryData.filter(item => {
      // Category filter
      if (inventoryFilters.category && inventoryFilters.category !== 'All Categories' && item.category !== inventoryFilters.category) {
        return false;
      }
      
      // Stock level filter - calculate overall product status based on total units vs total max capacity
      if (inventoryFilters.stockLevel && inventoryFilters.stockLevel !== 'All Levels') {
        const totalUnits = item.totalStock || 0;
        const totalMaxCapacity = item.variants?.reduce((sum: number, variant: any) => sum + (variant.maxCapacity || 0), 0) || 100;
        const overallStockLevel = getStockLevel(totalUnits, 0, totalMaxCapacity);
        
        if (overallStockLevel !== inventoryFilters.stockLevel) {
          return false;
        }
      }
      
      // Urgent only filter - show items with Critical stock level
      if (inventoryFilters.urgentOnly) {
        const totalUnits = item.totalStock || 0;
        const totalMaxCapacity = item.variants?.reduce((sum: number, variant: any) => sum + (variant.maxCapacity || 0), 0) || 100;
        const overallStockLevel = getStockLevel(totalUnits, 0, totalMaxCapacity);
        
        if (overallStockLevel !== 'Critical') {
          return false;
        }
      }
      
      // Smart search filter
      if (smartSearchTerm) {
        const term = smartSearchTerm.toLowerCase();
        if (smartSearchField === 'all') {
          return (
            item.name?.toLowerCase().includes(term) ||
            item.id?.toString().toLowerCase().includes(term) ||
            item.category?.toLowerCase().includes(term) ||
            item.sku?.toLowerCase().includes(term)
          );
        } else if (smartSearchField === 'name') {
          return item.name?.toLowerCase().includes(term);
        } else if (smartSearchField === 'id') {
          return item.id?.toString().toLowerCase().includes(term);
        } else if (smartSearchField === 'category') {
          return item.category?.toLowerCase().includes(term);
        } else if (smartSearchField === 'sku') {
          return item.sku?.toLowerCase().includes(term);
        }
        return false;
      }
      
      return true;
    });
  }, [inventoryData, inventoryFilters, smartSearchTerm, smartSearchField]);

  // Variant management helper functions
  const addVariant = (productData: any, setProductData: any) => {
    const newVariant = {
      size: "",
      sku: "",
      price: 0,
      stockLevel: 0,
      minThreshold: 10,
      maxCapacity: 100
    };
    setProductData({
      ...productData,
      variants: [...productData.variants, newVariant]
    });
  };

  const removeVariant = (productData: any, setProductData: any, index: number) => {
    setProductData({
      ...productData,
      variants: productData.variants.filter((_: any, i: number) => i !== index)
    });
  };

  const updateVariant = (productData: any, setProductData: any, index: number, field: string, value: any) => {
    const updatedVariants = [...productData.variants];
    updatedVariants[index] = {
      ...updatedVariants[index],
      [field]: value
    };
    setProductData({
      ...productData,
      variants: updatedVariants
    });
  };

  // Helper functions to refetch data
  const loadProducts = async () => {
    try {
      setProductsLoading(true);
      const productsData = await productService.getAllProducts();
      // Set the products array from the API response
      setProducts(productsData.products || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      setProductsError(error instanceof Error ? error.message : 'Failed to fetch products');
    } finally {
      setProductsLoading(false);
    }
  };

  const loadCatalogData = async () => {
    try {
      setCatalogLoading(true);
      const productsData = await productService.getAllProducts();
      
      // Transform API data to match the expected format for the UI
      const transformedData = productsData.products?.map((product: any) => ({
        id: product._id,
        name: product.name,
        category: product.category,
        sku: product.sku,
        price: product.formattedUnitPrice || product.unitPrice,
        description: product.description,
        status: "active", // Default status since it's not in the API
        image: `https://images.unsplash.com/photo-1576671081837-49000212a370?w=400&h=300&fit=crop&crop=center&auto=format&q=80&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D`,
        manufacturer: product.manufacturerId?.companyName || "Unknown Manufacturer",
        dimensions: "Standard Size", // Default since not in API
        material: "Medical Grade Material", // Default since not in API
        indications: ["General medical use"], // Default since not in API
        sizesAndParts: ["Various sizes available"], // Default since not in API
        productUrl: product.productUrl || "",
        _id: product._id,
        // Include productType and variants from backend
        productType: product.productType || "simple",
        variants: product.variants || [],
        baseStockLevel: product.baseStockLevel || 0,
        baseMinThreshold: product.baseMinThreshold || 10,
        baseMaxCapacity: product.baseMaxCapacity || 100
      })) || [];
      
      setProductCatalogData(transformedData);
    } catch (error) {
      console.error('Error fetching catalog data:', error);
      setCatalogError(error instanceof Error ? error.message : 'Failed to fetch catalog data');
    } finally {
      setCatalogLoading(false);
    }
  };

  const createSampleInventoryData = async () => {
    try {
      setInventoryLoading(true);
      setInventoryError(null);
      
      const practiceId = "000000000000000000000000"; // Default practice ID
      
      const response = await inventoryService.createSampleInventory(practiceId);
      
      if (response.inventory) {
        // Filter to only include active products
        const activeProducts = response.inventory.filter((product: any) => 
          product.status === 'active' || product.status === 'Active'
        );
        
        // Transform the product data to work with variants
        const transformedData = activeProducts.map((product: any) => {
          const unitPrice = product?.formattedUnitPrice || product?.unitPrice || 0;
          
          // Calculate overall product status based on variants
          const totalStock = product.totalStock || 0;
          const totalValue = product.totalValue || 0;
          const lowStockVariants = product.lowStockVariants || [];
          
          // Check if any variant has Critical stock status
          const hasCriticalStock = product.variants?.some((variant: any) => 
            variant.stockStatus === 'critical' || variant.stockStatus === 'Critical'
          ) || false;

          return {
            id: product._id,
            name: product.name || "Unknown Product",
            category: product.category || "Unknown Category",
            sku: product.sku || "N/A",
            description: product.description || "",
            hcpcCode: product.hcpcCode || "",
            unitPrice,
            totalStock,
            totalValue,
            lastRestocked: product.updatedAt ? new Date(product.updatedAt).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
            location: "Warehouse A-01", // Default location
            supplier: product.manufacturerId?.companyName || "Default Supplier",
            expiryDate: "2025-12-31", // Default expiry
            urgentReorder: hasCriticalStock,
            status: hasCriticalStock ? "Critical" : "Sufficient",
            variants: product.variants || [],
            // Base product stock fields
            baseStockLevel: product.baseStockLevel || 0,
            baseMinThreshold: product.baseMinThreshold || 10,
            baseMaxCapacity: product.baseMaxCapacity || 100,
            // Additional product information
            unitsSold: product.unitsSold || 0,
            revenue: product.formattedRevenue || product.revenue || 0,
            growthRate: product.formattedGrowthRate || product.growthRate || 0,
            returnRate: product.formattedReturnRate || product.returnRate || 0,
            totalReturns: product.totalReturns || 0,
            productUrl: product.productUrl || "",
            productStatus: product.status || "active",
            manufacturer: product.manufacturerId?.companyName || "Unknown Manufacturer",
            createdAt: product.createdAt ? new Date(product.createdAt).toISOString().split('T')[0] : "",
            updatedAt: product.updatedAt ? new Date(product.updatedAt).toISOString().split('T')[0] : ""
          };
        });
        
        setInventoryData(transformedData);
        toast({
          title: "Success",
          description: `Created ${response.inventory.length} sample inventory records!`,
        });
      }
    } catch (error) {
      console.error('Error creating sample inventory data:', error);
      setInventoryError(error instanceof Error ? error.message : 'Failed to create sample inventory data');
      toast({
        title: "Error",
        description: "Failed to create sample inventory data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setInventoryLoading(false);
    }
  };

  const loadInventoryData = async () => {
    try {
      setInventoryLoading(true);
      setInventoryError(null);
      
      // For now, we'll use a default practice ID. In a real app, this should come from user context
      const practiceId = "000000000000000000000000"; // Default practice ID
      
      const inventoryResponse = await inventoryService.getPracticeInventory(practiceId);
      
      // Filter to only include active products
      const activeProducts = inventoryResponse.filter((product: any) => 
        product.status === 'active' || product.status === 'Active'
      );
      
      // Transform the product data to work with variants
      const transformedData = activeProducts.map((product: any) => {
        // Determine product type and pricing
        const hasVariants = product.variants && product.variants.length > 0;
        const hasUnitPrice = product.unitPrice && product.unitPrice > 0;
        // const productType = hasVariants && hasUnitPrice ? "hybrid" : hasVariants ? "variant" : "simple";
        const productType = hasVariants ? "variant" : "simple"; // Hybrid support temporarily disabled
        
        // For simple products, use unitPrice; for variant products, use variants
        const unitPrice = productType === "simple" ? (product?.formattedUnitPrice || product?.unitPrice || 0) : 0;
        
        // Calculate overall product status based on variants or simple product stock
        const totalStock = product.totalStock || 0;
        const totalValue = product.totalValue || 0;
        const lowStockVariants = product.lowStockVariants || [];
        
        // Check if any variant has Critical stock status
        const hasCriticalStock = product.variants?.some((variant: any) => 
          variant.stockStatus === 'critical' || variant.stockStatus === 'Critical'
        ) || false;

        return {
          id: product._id,
          name: product.name || "Unknown Product",
          category: product.category || "Unknown Category",
          sku: product.sku || "N/A",
          description: product.description || "",
          hcpcCode: product.hcpcCode || "",
          unitPrice,
          totalStock,
          totalValue,
          lastRestocked: product.updatedAt ? new Date(product.updatedAt).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          location: "Warehouse A-01", // Default location
          supplier: product.manufacturerId?.companyName || "Default Supplier",
          expiryDate: "2025-12-31", // Default expiry
          urgentReorder: hasCriticalStock,
          status: hasCriticalStock ? "Critical" : "Sufficient",
          variants: product.variants || [],
          productType: productType, // Add product type to inventory data
          // Base product stock fields
          baseStockLevel: product.baseStockLevel || 0,
          baseMinThreshold: product.baseMinThreshold || 10,
          baseMaxCapacity: product.baseMaxCapacity || 100,
          // Additional product information
          unitsSold: product.unitsSold || 0,
          revenue: product.formattedRevenue || product.revenue || 0,
          growthRate: product.formattedGrowthRate || product.growthRate || 0,
          returnRate: product.formattedReturnRate || product.returnRate || 0,
          totalReturns: product.totalReturns || 0,
          productUrl: product.productUrl || "",
          productStatus: product.status || "active",
          manufacturer: product.manufacturerId?.companyName || "Unknown Manufacturer",
          createdAt: product.createdAt ? new Date(product.createdAt).toISOString().split('T')[0] : "",
          updatedAt: product.updatedAt ? new Date(product.updatedAt).toISOString().split('T')[0] : ""
        };
      });
      
      setInventoryData(transformedData);
    } catch (error) {
      console.error('Error fetching inventory data:', error);
      setInventoryError(error instanceof Error ? error.message : 'Failed to fetch inventory data');
    } finally {
      setInventoryLoading(false);
    }
  };

  const [newCategory, setNewCategory] = useState({ name: "", description: "" });
  const [editedCategory, setEditedCategory] = useState({ name: "", description: "" });
  
  
  // Product catalog data state
  const [productCatalogData, setProductCatalogData] = useState<any[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState<string | null>(null);

  const [revenueFilter, setRevenueFilter] = useState("Region");
  const [trendFilter, setTrendFilter] = useState("Region");
  const [selectedMetric, setSelectedMetric] = useState("providers");
  const [activeSection, setActiveSection] = useState("products");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeOrderTab, setActiveOrderTab] = useState("all");
  const [showTrackingDialog, setShowTrackingDialog] = useState(false);
  const [selectedOrderForTracking, setSelectedOrderForTracking] = useState<any>(null);
  const [activeProductTab, setActiveProductTab] = useState("products");
  const [showAddProductDialog, setShowAddProductDialog] = useState(false);
  const [showViewProductDialog, setShowViewProductDialog] = useState(false);
  const [showEditProductDialog, setShowEditProductDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [showOrderDetailsDialog, setShowOrderDetailsDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [selectedTrackingOrder, setSelectedTrackingOrder] = useState<any>(null);
  const [showTrackOrderDialog, setShowTrackOrderDialog] = useState(false);
  const [showInsuranceReviewList, setShowInsuranceReviewList] = useState(false);
  const [showShippingDelaysList, setShowShippingDelaysList] = useState(false);
  const [showInventoryShortageList, setShowInventoryShortageList] = useState(false);
  const initialNewProductState = {
    name: "",
    category: "",
    sku: "",
    price: "",
    description: "",
    productUrl: "",
    status: "active" as "active" | "inactive",
    productType: "simple" as "simple" | "variant", // Updated to support two types (hybrid support temporarily disabled)
    variants: [] as Array<{
      size: string;
      sku: string;
      price: number;
      stockLevel: number;
      minThreshold: number;
      maxCapacity: number;
    }>,
    // Base stock fields for simple products
    baseStockLevel: 0,
    baseMinThreshold: 10,
    baseMaxCapacity: 100
  };

  const [newProduct, setNewProduct] = useState(initialNewProductState);

  const handleAddProductDialogOpenChange = (open: boolean) => {
    setShowAddProductDialog(open);
    if (!open) {
      setNewProduct(initialNewProductState);
    }
  };
  
  const [editedProduct, setEditedProduct] = useState({
    name: "",
    category: "",
    sku: "",
    price: "",
    description: "",
    productUrl: "",
    status: "active" as "active" | "inactive",
    productType: "simple" as "simple" | "variant", // Updated to support two types (hybrid support temporarily disabled)
    variants: [] as Array<{
      size: string;
      sku: string;
      price: number;
      stockLevel: number;
      minThreshold: number;
      maxCapacity: number;
    }>,
    // Base stock fields for simple products
    baseStockLevel: 0,
    baseMinThreshold: 10,
    baseMaxCapacity: 100
  });

  const [orderFilters, setOrderFilters] = useState({
    providerType: "",
    dateRange: "",
    status: ""
  });
  const [providerFilters, setProviderFilters] = useState({
    type: "",
    status: "",
    renewalStatus: "",
    satisfactionLevel: ""
  });

  // Practice and Provider Details Table State
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [filters, setFilters] = useState({});
  const [activeProviderWidget, setActiveProviderWidget] = useState("");
  const [visibleColumns, setVisibleColumns] = useState({
    practiceName: true,
    practiceId: true,
    providerName: true,
    npi: true,
    specialty: true,
    city: true,
    state: true,
    phone: true,
    status: true,
    agreementType: true,
  });

  // Real-time notifications state
  const [notifications, setNotifications] = useState([
    {
      id: 1,
      type: "urgent",
      title: "Critical Inventory Alert",
      message: "Silver Alginate Rope below reorder threshold (12 units remaining)",
      timestamp: new Date(Date.now() - 5 * 60000), // 5 minutes ago
      priority: "high",
      category: "inventory",
      actionRequired: true
    },
    {
      id: 2,
      type: "opportunity",
      title: "New Provider Interest",
      message: "AZ Day Surgery requesting pricing for Advanced Wound Care line",
      timestamp: new Date(Date.now() - 15 * 60000), // 15 minutes ago
      priority: "medium",
      category: "sales",
      actionRequired: true
    },
    {
      id: 3,
      type: "success",
      title: "Contract Renewal",
      message: "The Surgical Shift renewed annual contract ($2.1M value)",
      timestamp: new Date(Date.now() - 30 * 60000), // 30 minutes ago
      priority: "low",
      category: "contracts",
      actionRequired: false
    }
  ]);

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setProductsLoading(true);
        setProductsError(null);
        const productsData = await productService.getAllProducts();
        // Set the products array from the API response
        setProducts(productsData.products || []);
      } catch (error) {
        console.error('Error fetching products:', error);
        setProductsError(error instanceof Error ? error.message : 'Failed to fetch products');
      } finally {
        setProductsLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setAnalyticsLoading(true);
        setAnalyticsError(null);
        const analyticsData = await inventoryService.getAnalytics();
        setAnalytics(analyticsData);
      } catch (error) {
        console.error('Error fetching analytics:', error);
        setAnalyticsError(error instanceof Error ? error.message : 'Failed to fetch analytics');
      } finally {
        setAnalyticsLoading(false);
      }
    };

    fetchAnalytics();
  }, []);

  // Fetch product catalog data
  useEffect(() => {
    const fetchProductCatalog = async () => {
  

      try {
        setCatalogLoading(true);
        setCatalogError(null);
        const productsData = await productService.getAllProducts();
        
        // Transform API data to match the expected format for the UI
        const transformedData = productsData.products?.map((product: any) => ({
          id: product._id,
          name: product.name,
          category: product.category,
          sku: product.sku,
          price: product.formattedUnitPrice || product.unitPrice,
          description: product.description,
          status: product.status || "active", // Use actual status from API
          image: `https://images.unsplash.com/photo-1576671081837-49000212a370?w=400&h=300&fit=crop&crop=center&auto=format&q=80&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D`,
          manufacturer: product.manufacturerId?.companyName || "Unknown Manufacturer",
          dimensions: "Standard Size", // Default since not in API
          material: "Medical Grade Material", // Default since not in API
          indications: ["General medical use"], // Default since not in API
          contraindications: ["None specified"], // Default since not in API
          sterile: true, // Default since not in API
          shelfLife: "2 years", // Default since not in API
          productUrl: product.productUrl || "",
          _id: product._id,
          storage: "Store in cool, dry place", // Default since not in API
          certifications: ["FDA Approved"], // Default since not in API
          // Include productType and variants from backend
          productType: product.productType || "simple",
          variants: product.variants || [],
          baseStockLevel: product.baseStockLevel || 0,
          baseMinThreshold: product.baseMinThreshold || 10,
          baseMaxCapacity: product.baseMaxCapacity || 100,
          lastUpdated: product.updatedAt ? new Date(product.updatedAt).toISOString().split('T')[0] : "2024-01-01",
          // Add API-specific fields
          unitsSold: product.unitsSold || 0,
          revenue: product.formattedRevenue || 0,
          growthRate: product.formattedGrowthRate || 0
        })) || [];
        
        setProductCatalogData(transformedData);
      } catch (error) {
        console.error('Error fetching product catalog:', error);
        setCatalogError(error instanceof Error ? error.message : 'Failed to fetch product catalog');
      } finally {
        setCatalogLoading(false);
      }
    };

    fetchProductCatalog();
  }, []);

  // Load inventory data when component mounts
  useEffect(() => {
    loadInventoryData();
  }, []);

  // Fetch data when switching tabs
  useEffect(() => {
    const fetchTabData = async () => {
      switch (activeProductTab) {
        case "products":
          await loadProducts();
          break;
        case "inventory":
          await loadInventoryData();
          break;
        case "categories":
          // Categories data is already loaded via useConfigurationOptions
          break;
        default:
          break;
      }
    };

    fetchTabData();
  }, [activeProductTab]);

  // Helper function to refresh catalog data
  const refreshCatalogData = async () => {
    try {
      const catalogData = await productService.getAllProducts();
      const transformedData = catalogData.products?.map((product: any) => ({
        id: product._id,
        name: product.name,
        category: product.category,
        sku: product.sku,
        price: product.formattedUnitPrice || product.unitPrice,
        description: product.description,
        status: product.status || "active",
        image: `https://images.unsplash.com/photo-1576671081837-49000212a370?w=400&h=300&fit=crop&crop=center&auto=format&q=80&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D`,
        manufacturer: product.manufacturerId?.companyName || "Unknown Manufacturer",
        dimensions: "Standard Size",
        material: "Medical Grade Material",
        indications: ["General medical use"],
        contraindications: ["None specified"],
        sterile: true,
        shelfLife: "2 years",
        productUrl: product.productUrl || "",
        _id: product._id,
        storage: "Store in cool, dry place",
        certifications: ["FDA Approved"],
        // Include productType and variants from backend
        productType: product.productType || "simple",
        variants: product.variants || [],
        baseStockLevel: product.baseStockLevel || 0,
        baseMinThreshold: product.baseMinThreshold || 10,
        baseMaxCapacity: product.baseMaxCapacity || 100,
        lastUpdated: product.updatedAt ? new Date(product.updatedAt).toISOString().split('T')[0] : "2024-01-01",
        unitsSold: product.unitsSold || 0,
        revenue: product.formattedRevenue || 0,
        growthRate: product.formattedGrowthRate || 0
      })) || [];
      setProductCatalogData(transformedData);
    } catch (error) {
      console.error('Error refreshing catalog data:', error);
    }
  };

  // Product management functions
  const handleCreateProduct = async () => {
    if (!newProduct.name || !newProduct.category || !newProduct.sku) {
      toast({ title: "Please fill in all required fields", variant: "destructive" });
      return;
    }

    // Validate category consistency
    const categoryValidation = validateCategoryConsistency(findCategoryLabelByKey(newProduct.category));
    if (!categoryValidation.isValid) {
      toast({ 
        title: "Category Error", 
        description: categoryValidation.message,
        variant: "destructive" 
      });
      return;
    }

    // Validate product type requirements
    if (newProduct.productType === "simple" && !newProduct.price) {
      toast({ title: "Please enter a price for the product", variant: "destructive" });
      return;
    }
    
    if (newProduct.productType === "variant" && newProduct.variants.length === 0) {
      toast({ title: "Please add at least one variant", variant: "destructive" });
      return;
    }
    
    // Validate variant fields for variant products
    if (newProduct.productType === "variant") {
      for (let i = 0; i < newProduct.variants.length; i++) {
        const variant = newProduct.variants[i];
        if (!variant.size || variant.size.trim() === "") {
          toast({ 
            title: "Variant Validation Error", 
            description: `Variant ${i + 1}: Size is required`,
            variant: "destructive" 
          });
          return;
        }
        if (!variant.sku || variant.sku.trim() === "") {
          toast({ 
            title: "Variant Validation Error", 
            description: `Variant ${i + 1}: SKU is required`,
            variant: "destructive" 
          });
          return;
        }
        if (variant.price === undefined || variant.price === null || variant.price < 0) {
          toast({ 
            title: "Variant Validation Error", 
            description: `Variant ${i + 1}: Price must be 0 or greater`,
            variant: "destructive" 
          });
          return;
        }
      }
    }
    
    // Hybrid product validation temporarily disabled
    // if (newProduct.productType === "hybrid" && !newProduct.price && newProduct.variants.length === 0) {
    //   toast({ title: "Please enter a base price and/or add at least one variant", variant: "destructive" });
    //   return;
    // }

    try {
      setIsCreatingProduct(true);
      
      // Convert category key to label format for backend
      const categoryLabel = findCategoryLabelByKey(newProduct.category);
      
      // Prepare product data based on product type
      const productData: any = {
        name: newProduct.name,
        category: categoryLabel,
        sku: newProduct.sku,
        description: newProduct.description || undefined,
        productUrl: newProduct.productUrl || undefined,
        status: newProduct.status || 'active',
        productType: newProduct.productType,
        variants: newProduct.variants || [],
        // Base stock fields for simple products
        baseStockLevel: newProduct.baseStockLevel || 0,
        baseMinThreshold: newProduct.baseMinThreshold || 10,
        baseMaxCapacity: newProduct.baseMaxCapacity || 100
      };

      // Add pricing based on product type
      if (newProduct.productType === "simple") {
        // Simple product: use unitPrice, clear variants
        productData.unitPrice = parseFloat(newProduct.price);
        productData.variants = []; // No variants for simple products
      } else if (newProduct.productType === "variant") {
        // Variant product: use variants, no unitPrice
        productData.unitPrice = undefined; // No unit price for variant products
        // Clean variants to only include allowed fields (remove _id and other MongoDB fields)
        productData.variants = newProduct.variants.map(variant => ({
          size: variant.size,
          sku: variant.sku,
          price: variant.price,
          stockLevel: variant.stockLevel || 0,
          minThreshold: variant.minThreshold || 0,
          maxCapacity: variant.maxCapacity || 100
        }));
      // } else if (newProduct.productType === "hybrid") {
      //   // Hybrid product: use both unitPrice and variants
      //   productData.unitPrice = newProduct.price ? parseFloat(newProduct.price) : undefined;
      //   productData.variants = newProduct.variants;
      }

      await productService.createProduct(productData);
      toast({ title: "Product Added", description: `${newProduct.name} has been added successfully` });
      setShowAddProductDialog(false);
      setNewProduct({ 
        name: "", 
        category: "", 
        sku: "", 
        price: "", 
        description: "",
        productUrl: "",
        status: "active",
        productType: "simple",
        variants: [],
        // Base stock fields for simple products
        baseStockLevel: 0,
        baseMinThreshold: 10,
        baseMaxCapacity: 100
      });
      
      // Refresh component state instead of reloading page
      await loadProducts();
      await loadCatalogData();
      // Also refresh the catalog data for the active tab
      await refreshCatalogData();
      // Refresh category options in case counts changed
      await refetchCategories();
    } catch (error) {
      console.error('Error creating product:', error);
      toast({ 
        title: "Error", 
        description: error instanceof Error ? error.message : "Failed to create product", 
        variant: "destructive" 
      });
    } finally {
      setIsCreatingProduct(false);
    }
  };

  const handleUpdateProduct = async () => {
    if (!editedProduct.name || !editedProduct.category || !editedProduct.sku || !selectedProduct) {
      toast({ title: "Please fill in all required fields", variant: "destructive" });
      return;
    }

    // Validate category consistency
    const categoryValidation = validateCategoryConsistency(findCategoryLabelByKey(editedProduct.category));
    if (!categoryValidation.isValid) {
      toast({ 
        title: "Category Error", 
        description: categoryValidation.message,
        variant: "destructive" 
      });
      return;
    }

    // Validate product type requirements
    if (editedProduct.productType === "simple" && !editedProduct.price) {
      toast({ title: "Please enter a price for the product", variant: "destructive" });
      return;
    }
    
    if (editedProduct.productType === "variant" && editedProduct.variants.length === 0) {
      toast({ title: "Please add at least one variant", variant: "destructive" });
      return;
    }
    
    // Validate variant fields for variant products
    if (editedProduct.productType === "variant") {
      for (let i = 0; i < editedProduct.variants.length; i++) {
        const variant = editedProduct.variants[i];
        if (!variant.size || variant.size.trim() === "") {
          toast({ 
            title: "Variant Validation Error", 
            description: `Variant ${i + 1}: Size is required`,
            variant: "destructive" 
          });
          return;
        }
        if (!variant.sku || variant.sku.trim() === "") {
          toast({ 
            title: "Variant Validation Error", 
            description: `Variant ${i + 1}: SKU is required`,
            variant: "destructive" 
          });
          return;
        }
        if (variant.price === undefined || variant.price === null || variant.price < 0) {
          toast({ 
            title: "Variant Validation Error", 
            description: `Variant ${i + 1}: Price must be 0 or greater`,
            variant: "destructive" 
          });
          return;
        }
      }
    }
    
    // Hybrid product validation temporarily disabled
    // if (editedProduct.productType === "hybrid" && !editedProduct.price && editedProduct.variants.length === 0) {
    //   toast({ title: "Please enter a base price and/or add at least one variant", variant: "destructive" });
    //   return;
    // }

    try {
      setIsUpdatingProduct(true);
      
      // Convert category key back to label format for backend
      const categoryLabel = findCategoryLabelByKey(editedProduct.category);
      
      // Prepare product data based on product type
      const productData: any = {
        name: editedProduct.name,
        category: categoryLabel,
        sku: editedProduct.sku,
        description: editedProduct.description || undefined,
        productUrl: editedProduct.productUrl || undefined,
        status: (editedProduct.status || 'active').toLowerCase(),
        productType: editedProduct.productType,
        variants: editedProduct.variants || [],
        // Base stock fields for simple products
        baseStockLevel: editedProduct.baseStockLevel,
        baseMinThreshold: editedProduct.baseMinThreshold,
        baseMaxCapacity: editedProduct.baseMaxCapacity
      };

      // Add pricing based on product type
      if (editedProduct.productType === "simple") {
        // Simple product: use unitPrice, clear variants
        productData.unitPrice = parseFloat(editedProduct.price);
        productData.variants = []; // No variants for simple products
      } else if (editedProduct.productType === "variant") {
        // Variant product: use variants, no unitPrice
        productData.unitPrice = undefined; // No unit price for variant products
        // Clean variants to only include allowed fields (remove _id and other MongoDB fields)
        productData.variants = editedProduct.variants.map(variant => ({
          size: variant.size,
          sku: variant.sku,
          price: variant.price,
          stockLevel: variant.stockLevel || 0,
          minThreshold: variant.minThreshold || 0,
          maxCapacity: variant.maxCapacity || 100
        }));
      // } else if (editedProduct.productType === "hybrid") {
      //   // Hybrid product: use both unitPrice and variants
      //   productData.unitPrice = editedProduct.price ? parseFloat(editedProduct.price) : undefined;
      //   productData.variants = editedProduct.variants;
      }

      await productService.updateProduct(selectedProduct._id, productData);
      toast({ title: "Product Updated", description: `${editedProduct.name} has been updated successfully` });
      setShowEditProductDialog(false);
      
      // Refresh component state instead of reloading page
      await loadProducts();
      await loadCatalogData();
      // Also refresh the catalog data for the active tab
      await refreshCatalogData();
      // Refresh category options in case counts changed
      await refetchCategories();
    } catch (error) {
      console.error('Error updating product:', error);
      toast({ 
        title: "Error", 
        description: error instanceof Error ? error.message : "Failed to update product", 
        variant: "destructive" 
      });
    } finally {
      setIsUpdatingProduct(false);
    }
  };

  const handleDeleteProduct = async (product: any) => {
    if (!confirm(`Are you sure you want to delete "${product.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setIsDeletingProduct(true);
      await productService.deleteProduct(product._id);
      toast({ title: "Product Deleted", description: `${product.name} has been deleted successfully` });
      
      // Refresh component state instead of reloading page
      await loadProducts();
      await loadCatalogData();
      // Also refresh the catalog data for the active tab
      await refreshCatalogData();
      // Refresh category options in case counts changed
      await refetchCategories();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast({ 
        title: "Error", 
        description: error instanceof Error ? error.message : "Failed to delete product", 
        variant: "destructive" 
      });
    } finally {
      setIsDeletingProduct(false);
    }
  };

  const handleActivateProduct = async (product: any) => {
    try {
      setIsActivatingProduct(true);
      const productId = product._id || product.id;
      await productService.updateProduct(productId, { status: 'active' });
      toast({ title: "Product Activated", description: `${product.name} has been activated successfully` });
      
      // Refresh component state instead of reloading page
      await loadProducts();
      await loadCatalogData();
      // Also refresh the catalog data for the active tab
      await refreshCatalogData();
    } catch (error) {
      console.error('Error activating product:', error);
      toast({ 
        title: "Error", 
        description: "Failed to activate product", 
        variant: "destructive" 
      });
    } finally {
      setIsActivatingProduct(false);
    }
  };

  const handleDeactivateProduct = async (product: any) => {
    try {
      setIsDeactivatingProduct(true);
      const productId = product._id || product.id;
      await productService.updateProduct(productId, { status: 'inactive' });
      toast({ title: "Product Deactivated", description: `${product.name} has been deactivated successfully` });
      
      // Refresh component state instead of reloading page
      await loadProducts();
      await loadCatalogData();
      // Also refresh the catalog data for the active tab
      await refreshCatalogData();
    } catch (error) {
      console.error('Error deactivating product:', error);
      toast({ 
        title: "Error", 
        description: "Failed to deactivate product", 
        variant: "destructive" 
      });
    } finally {
      setIsDeactivatingProduct(false);
    }
  };

  const handleEditProduct = (product: any) => {
    // Ensure we have the correct ID field (_id for API calls)
    const productWithId = {
      ...product,
      _id: product._id || product.id
    };
    
    // Use productType from database, fallback to determination logic for backward compatibility
    const productType = product.productType || (() => {
      const hasVariants = product.variants && product.variants.length > 0;
      const hasUnitPrice = product.unitPrice && product.unitPrice > 0;
      return hasVariants && hasUnitPrice ? "hybrid" : hasVariants ? "variant" : "simple";
    })();
    
    
    // Find the correct category key by matching the label
    const categoryKey = findCategoryKeyByLabel(product.category);
    
    setSelectedProduct(productWithId);
    const editedProductData = {
      name: product.name || "",
      category: categoryKey,
      sku: product.sku || "",
      price: product.unitPrice?.toString() || product.price?.toString() || "",
      description: product.description || "",
      productUrl: product.productUrl || "",
      status: product.status || "active",
      productType: productType,
      variants: product.variants || [],
      // Base stock fields for simple products
      baseStockLevel: product.baseStockLevel || 0,
      baseMinThreshold: product.baseMinThreshold || 10,
      baseMaxCapacity: product.baseMaxCapacity || 100
    };
    
    
    setEditedProduct(editedProductData);
    setShowEditProductDialog(true);
  };

  // Edit Stock handlers
  const handleEditStock = async (product: any) => {
    try {
      setSelectedProductForStock(product);
      
      // Fetch inventory data from database
      // Note: In a real app, you'd need to get the practiceId from context or props
      const practiceId = "000000000000000000000000"; // Default practice ID
      
      try {
        const inventoryData = await inventoryService.getInventoryStock(practiceId, product._id || product.id);
        setSelectedProductInventory(inventoryData);
      } catch (error) {
        // If no inventory record exists, create default values
        console.log('No inventory record found, using defaults');
        setSelectedProductInventory({
          currentStock: 0,
          minStock: 10,
          maxStock: 100
        });
      }
      
      setShowEditStockModal(true);
    } catch (error) {
      console.error('Error opening edit stock modal:', error);
      toast({
        title: "Error",
        description: "Failed to load inventory data. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Edit Stock handler for inventory items (with existing stock data)
  const handleEditInventoryStock = async (inventoryItem: any, variant: any) => {
    try {
      // Create a product object from inventory item
      const product = {
        _id: inventoryItem.id,
        name: inventoryItem.name,
        sku: inventoryItem.sku,
        category: inventoryItem.category,
        unitPrice: inventoryItem.unitPrice
      };
      
      setSelectedProductForStock(product);
      
      // Use the variant data
      setSelectedProductInventory({
        size: variant.size,
        sku: variant.sku,
        price: variant.price,
        stockLevel: variant.stockLevel,
        minThreshold: variant.minThreshold,
        maxCapacity: variant.maxCapacity
      });
      
      setShowEditStockModal(true);
    } catch (error) {
      console.error('Error opening edit stock modal:', error);
      toast({
        title: "Error",
        description: "Failed to load inventory data. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleSaveStock = async (productId: string, variantData: any) => {
    try {
      setIsUpdatingStock(true);
      
      // Debug: Log the productId being sent
      console.log('Sending productId to API:', productId, 'Type:', typeof productId);
      console.log('ProductId length:', productId?.length);
      
      // Validate that productId is a valid MongoDB ObjectId format (24 hex characters)
      const objectIdRegex = /^[0-9a-fA-F]{24}$/;
      if (!objectIdRegex.test(productId)) {
        console.error('Invalid productId format:', productId);
        toast({
          title: "Error",
          description: "Invalid product ID format. Please refresh the page and try again.",
          variant: "destructive"
        });
        return;
      }
      
      // Find the product in inventory data to determine its type
      const product = inventoryData.find(item => item.id === productId);
      if (!product) {
        console.error('Product not found in inventory data:', productId);
        toast({
          title: "Error",
          description: "Product not found. Please refresh the page and try again.",
          variant: "destructive"
        });
        return;
      }
      
      // Check if stock quantity actually changed
      let stockQuantityChanged = false;
      
      if (product.productType === "simple") {
        // For simple products, check if baseStockLevel changed
        const currentBaseStock = product.baseStockLevel || 0;
        const newBaseStock = variantData.baseStockLevel || variantData.stockLevel || 0;
        stockQuantityChanged = currentBaseStock !== newBaseStock;
      } else if (product.productType === "variant") {
        // For variant products, check if any variant stock levels changed
        if (variantData.size) {
          // Single variant update
          const currentVariant = product.variants?.find((v: any) => v.size === variantData.size);
          if (currentVariant) {
            stockQuantityChanged = currentVariant.stockLevel !== variantData.stockLevel;
          }
        } else if (variantData.variants) {
          // Multiple variants update
          stockQuantityChanged = variantData.variants.some((newVariant: any) => {
            const currentVariant = product.variants?.find((v: any) => v.size === newVariant.size);
            return currentVariant && currentVariant.stockLevel !== newVariant.stockLevel;
          });
        }
      }
      
      console.log('Stock quantity changed:', stockQuantityChanged);
      
      let updatedProduct: any;
      
      // Determine which API endpoint to use based on product type and stock type
      if (product.productType === "simple") {
        // For Simple Products, use the base product stock update endpoint
        console.log('Updating simple product stock:', productId, variantData);
        updatedProduct = await inventoryService.updateBaseProductStock(productId, variantData);
      } else if (product.productType === "variant") {
        // For Variant Products, use the variant update endpoint
        console.log('Updating variant product stock:', productId, variantData);
        updatedProduct = await inventoryService.updateProductVariant(productId, variantData);
      // } else if (product.productType === "hybrid") {
      //   // For Hybrid Products, determine based on the stock type being updated
      //   if (variantData.size === undefined) {
      //     // Base stock update (no size field means base stock)
      //     console.log('Updating hybrid product base stock:', productId, variantData);
      //     updatedProduct = await inventoryService.updateBaseProductStock(productId, variantData);
      //   } else {
      //     // Variant stock update (has size field means variant stock)
      //     console.log('Updating hybrid product variant stock:', productId, variantData);
      //     updatedProduct = await inventoryService.updateProductVariant(productId, variantData);
      //   }
      }
      
      // Update the inventory data in state with the updated product
      setInventoryData(prevData => 
        prevData.map(item => 
          item.id === productId 
            ? {
                ...item,
                variants: updatedProduct.variants || item.variants,
                baseStockLevel: updatedProduct.baseStockLevel || item.baseStockLevel,
                baseMinThreshold: updatedProduct.baseMinThreshold || item.baseMinThreshold,
                baseMaxCapacity: updatedProduct.baseMaxCapacity || item.baseMaxCapacity,
                totalStock: updatedProduct.totalStock || item.totalStock,
                totalValue: updatedProduct.totalValue || item.totalValue,
                // Only update lastRestocked if stock quantity actually changed
                lastRestocked: stockQuantityChanged ? new Date().toISOString() : item.lastRestocked,
                urgentReorder: updatedProduct.variants?.some((variant: any) => 
                  variant.stockStatus === 'critical' || variant.stockStatus === 'Critical'
                ) || false,
                status: updatedProduct.variants?.some((variant: any) => 
                  variant.stockStatus === 'critical' || variant.stockStatus === 'Critical'
                ) ? "Critical" : "Sufficient"
              }
            : item
        )
      );
      
      // Also refresh the catalog data
      await loadCatalogData();
      
      toast({
        title: "Success",
        description: "Stock updated successfully!",
      });
    } catch (error) {
      console.error('Error updating stock:', error);
      throw error; // Re-throw to let the modal handle the error display
    } finally {
      setIsUpdatingStock(false);
    }
  };

  // Category management functions
  const handleCreateCategory = async () => {
    if (!newCategory.name.trim()) {
      toast({ title: "Please enter a category name", variant: "destructive" });
      return;
    }

    if (!user?.id) {
      toast({ title: "User not authenticated", variant: "destructive" });
      return;
    }

    try {
      setIsCreatingCategory(true);
      const categoryData = {
        category: 'product_categories',
        key: normalizeCategoryNameToKey(newCategory.name),
        label: newCategory.name,
        value: newCategory.name,
        description: newCategory.description || undefined,
        isActive: true,
        sortOrder: 0
      };

      const result = await configurationService.createConfiguration(categoryData);
      if (result.success) {
        toast({ title: "Category Added", description: `${newCategory.name} has been added successfully` });
        setShowAddCategoryDialog(false);
        setNewCategory({ name: "", description: "" });
        // Refresh category options
        await refetchCategories();
        // Refresh categories
        await loadCatalogData();
        // Also refresh the catalog data for the active tab
        await refreshCatalogData();
      } else {
        toast({ 
          title: "Error", 
          description: result.message || "Failed to create category", 
          variant: "destructive" 
        });
      }
    } catch (error) {
      console.error('Error creating category:', error);
      toast({ 
        title: "Error", 
        description: "Failed to create category", 
        variant: "destructive" 
      });
    } finally {
      setIsCreatingCategory(false);
    }
  };

  const handleUpdateCategory = async () => {
    if (!editedCategory.name.trim() || !selectedCategoryForEdit) {
      toast({ title: "Please enter a category name", variant: "destructive" });
      return;
    }

    // Check if category name is changing and warn about product sync
    const isNameChanging = selectedCategoryForEdit.label !== editedCategory.name;
    if (isNameChanging) {
      const confirmed = confirm(
        `You are changing the category name from "${selectedCategoryForEdit.label}" to "${editedCategory.name}".\n\n` +
        `This will update all products currently using this category. Are you sure you want to continue?`
      );
      if (!confirmed) {
        return;
      }
    }

    try {
      setIsUpdatingCategory(true);
      const updateData = {
        label: editedCategory.name,
        value: editedCategory.name,
        description: editedCategory.description || undefined
      };

      // Use the sync endpoint for product categories to update associated products
      const result = await configurationService.updateConfigurationWithSync(
        'product_categories',
        selectedCategoryForEdit.key,
        updateData
      );
      
      if (result.success) {
        const productsUpdated = (result.data as any)?.productsUpdated || 0;
        const message = productsUpdated > 0 
          ? `${editedCategory.name} has been updated successfully. ${productsUpdated} products were also updated.`
          : `${editedCategory.name} has been updated successfully.`;
        
        toast({ 
          title: "Category Updated", 
          description: message,
          duration: productsUpdated > 0 ? 6000 : 4000 // Show longer for product sync
        });
        setShowEditCategoryDialog(false);
        setSelectedCategoryForEdit(null);
        setEditedCategory({ name: "", description: "" });
        // Refresh category options
        await refetchCategories();
        // Refresh categories
        await loadCatalogData();
        // Also refresh the catalog data for the active tab
        await refreshCatalogData();
      } else {
        toast({ 
          title: "Error", 
          description: result.message || "Failed to update category", 
          variant: "destructive" 
        });
      }
    } catch (error) {
      console.error('Error updating category:', error);
      toast({ 
        title: "Error", 
        description: "Failed to update category", 
        variant: "destructive" 
      });
    } finally {
      setIsUpdatingCategory(false);
    }
  };

  const handleDeleteCategory = async (category: any) => {
    if (!confirm(`Are you sure you want to delete "${category.label}"? This will move all associated products to "Other" category. This action cannot be undone.`)) {
      return;
    }

    try {
      setIsDeletingCategory(true);
      const result = await configurationService.deleteConfigurationWithSync('product_categories', category.key);
      
      if (result.success) {
        const productsUpdated = (result.data as any)?.productsUpdated || 0;
        toast({ 
          title: "Category Deleted", 
          description: `${category.label} has been deleted successfully. ${productsUpdated} products moved to "Other" category.` 
        });
        // Refresh category options
        await refetchCategories();
        // Refresh categories
        await loadCatalogData();
        // Also refresh the catalog data for the active tab
        await refreshCatalogData();
      } else {
        toast({ 
          title: "Error", 
          description: result.message || "Failed to delete category", 
          variant: "destructive" 
        });
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      toast({ 
        title: "Error", 
        description: "Failed to delete category", 
        variant: "destructive" 
      });
    } finally {
      setIsDeletingCategory(false);
    }
  };

  const handleEditCategory = (category: any) => {
    setSelectedCategoryForEdit(category);
    setEditedCategory({
      name: category.label,
      description: category.description || ""
    });
    setShowEditCategoryDialog(true);
  };


  // Sample data for insurance declined orders
  const insuranceDeclinedOrders = [
    {
      id: "ORD-2024-001",
      patientName: "Sarah Johnson",
      patientId: "PT-4567",
      providerName: "Dr. Michael Chen",
      practiceName: "City Medical Center",
      insuranceCompany: "BlueCross BlueShield",
      policyNumber: "BC123456789",
      products: [
        { name: "Hydrocolloid Dressing", sku: "HC-500", quantity: 30, unitPrice: 15.50 },
        { name: "Silver Foam Dressing", sku: "SF-200", quantity: 15, unitPrice: 28.75 }
      ],
      totalAmount: 896.25,
      declineReason: "Prior authorization required for quantities over 10 units",
      declineDate: "2024-01-15",
      orderDate: "2024-01-10",
      urgencyLevel: "High",
      contactAttempts: 2,
      lastContactDate: "2024-01-16",
      status: "pending_review",
      nextAction: "Contact insurance for prior auth requirements",
      notes: "Patient requires dressings for chronic wound care. Provider indicated urgent need."
    },
    {
      id: "ORD-2024-002",
      patientName: "Robert Williams",
      patientId: "PT-7890",
      providerName: "Dr. Lisa Rodriguez",
      practiceName: "Advanced Wound Care Clinic",
      insuranceCompany: "United Healthcare",
      policyNumber: "UH987654321",
      products: [
        { name: "Alginate Rope", sku: "AR-100", quantity: 20, unitPrice: 22.00 },
        { name: "Antimicrobial Gel", sku: "AG-300", quantity: 5, unitPrice: 45.50 }
      ],
      totalAmount: 667.50,
      declineReason: "Coverage limits exceeded for current benefit period",
      declineDate: "2024-01-14",
      orderDate: "2024-01-08",
      urgencyLevel: "Sufficient",
      contactAttempts: 1,
      lastContactDate: "2024-01-15",
      status: "awaiting_documentation",
      nextAction: "Request benefit details and coverage remaining",
      notes: "Patient's annual wound care benefit may be exhausted. Need to verify remaining coverage."
    },
    {
      id: "ORD-2024-003",
      patientName: "Maria Garcia",
      patientId: "PT-2345",
      providerName: "Dr. James Thompson",
      practiceName: "Regional Hospital",
      insuranceCompany: "Aetna",
      policyNumber: "AE555666777",
      products: [
        { name: "Compression Bandage", sku: "CB-150", quantity: 12, unitPrice: 18.25 },
        { name: "Wound Cleanser", sku: "WC-400", quantity: 3, unitPrice: 32.00 }
      ],
      totalAmount: 315.00,
      declineReason: "Product not covered under current plan benefits",
      declineDate: "2024-01-12",
      orderDate: "2024-01-07",
      urgencyLevel: "Low",
      contactAttempts: 0,
      lastContactDate: null,
      status: "new",
      nextAction: "Review plan benefits and suggest covered alternatives",
      notes: "May need to substitute with covered equivalent products."
    }
  ];

  // Sample data for shipping delays
  const shippingDelaysOrders = [
    {
      id: "ORD-2024-004",
      patientName: "Jennifer Davis",
      patientId: "PT-8901",
      providerName: "Dr. Amanda Wilson",
      practiceName: "Coastal Medical Group",
      products: [
        { name: "Foam Dressing", sku: "FD-100", quantity: 25, unitPrice: 12.50 },
        { name: "Medical Tape", sku: "MT-250", quantity: 10, unitPrice: 8.75 }
      ],
      totalAmount: 400.00,
      originalShipDate: "2024-01-12",
      currentEstimate: "2024-01-20",
      delayReason: "Severe weather conditions affecting logistics network",
      carrier: "FedEx Express",
      trackingNumber: "7234567890123456",
      urgencyLevel: "High",
      delayDays: 8,
      status: "in_transit_delayed",
      lastUpdate: "2024-01-16",
      notes: "Patient has urgent wound care needs. Exploring expedited shipping options.",
      carrierContact: "1-800-463-3339",
      alternativeOptions: "Available for local pickup at Memphis hub"
    },
    {
      id: "ORD-2024-005",
      patientName: "Michael Brown",
      patientId: "PT-5678",
      providerName: "Dr. Kevin Park",
      practiceName: "Metro Health Center",
      products: [
        { name: "Compression Wrap", sku: "CW-300", quantity: 15, unitPrice: 18.25 },
        { name: "Gauze Pads", sku: "GP-400", quantity: 50, unitPrice: 2.50 }
      ],
      totalAmount: 398.75,
      originalShipDate: "2024-01-14",
      currentEstimate: "2024-01-19",
      delayReason: "Transportation strike in distribution region",
      carrier: "UPS Ground",
      trackingNumber: "1Z999AA1234567890",
      urgencyLevel: "Sufficient",
      delayDays: 5,
      status: "warehouse_hold",
      lastUpdate: "2024-01-17",
      notes: "Alternative carrier arrangements being explored.",
      carrierContact: "1-800-742-5877",
      alternativeOptions: "Switch to air freight available (+$45)"
    }
  ];

  // Sample data for inventory shortage
  const inventoryShortageOrders = [
    {
      id: "ORD-2024-011",
      patientName: "Thomas Anderson",
      patientId: "PT-7531",
      providerName: "Dr. Sarah Williams",
      practiceName: "Phoenix Medical Group",
      products: [
        { name: "Silver Antimicrobial Dressing", sku: "SAD-800", quantity: 15, unitPrice: 45.00, stockLevel: 0, expectedRestock: "2024-01-25" },
        { name: "Advanced Foam Dressing", sku: "AFD-900", quantity: 20, unitPrice: 35.50, stockLevel: 3, expectedRestock: "2024-01-22" }
      ],
      totalAmount: 1385.00,
      orderDate: "2024-01-18",
      originalExpectedDate: "2024-01-20",
      newExpectedDate: "2024-01-26",
      shortageReason: "Manufacturing delay due to raw material shortage",
      urgencyLevel: "High",
      delayDays: 6,
      status: "awaiting_restock",
      lastUpdate: "2024-01-18",
      notes: "Critical patient with infected wound requiring specialized antimicrobial treatment.",
      supplierContact: "Advanced Wound Solutions - 1-800-123-4567",
      alternativeOptions: "Similar antimicrobial products available from different supplier"
    },
    {
      id: "ORD-2024-012",
      patientName: "Maria Gonzalez",
      patientId: "PT-9642",
      providerName: "Dr. Michael Chen",
      practiceName: "Desert Health Associates",
      products: [
        { name: "Calcium Alginate Rope", sku: "CAR-700", quantity: 25, unitPrice: 28.75, stockLevel: 0, expectedRestock: "2024-01-30" },
        { name: "Hydrocolloid Paste", sku: "HP-400", quantity: 10, unitPrice: 22.50, stockLevel: 2, expectedRestock: "2024-01-24" }
      ],
      totalAmount: 943.75,
      orderDate: "2024-01-17",
      originalExpectedDate: "2024-01-19",
      newExpectedDate: "2024-01-31",
      shortageReason: "Supplier production facility maintenance shutdown",
      urgencyLevel: "Sufficient",
      delayDays: 12,
      status: "production_delay",
      lastUpdate: "2024-01-17",
      notes: "Deep wound care patient, alternative products may be suitable.",
      supplierContact: "ConvaTec Medical - 1-800-422-8811",
      alternativeOptions: "Collagen-based alternatives in stock"
    },
    {
      id: "ORD-2024-013",
      patientName: "James Mitchell",
      patientId: "PT-3579",
      providerName: "Dr. Linda Davis",
      practiceName: "Riverside Medical Center",
      products: [
        { name: "Negative Pressure Dressing", sku: "NPD-1000", quantity: 5, unitPrice: 85.00, stockLevel: 0, expectedRestock: "2024-02-05" },
        { name: "Suction Canisters", sku: "SC-200", quantity: 12, unitPrice: 15.75, stockLevel: 0, expectedRestock: "2024-02-03" }
      ],
      totalAmount: 614.00,
      orderDate: "2024-01-16",
      originalExpectedDate: "2024-01-18",
      newExpectedDate: "2024-02-06",
      shortageReason: "Global supply chain disruption for specialized equipment",
      urgencyLevel: "High",
      delayDays: 19,
      status: "supply_chain_disruption",
      lastUpdate: "2024-01-18",
      notes: "Post-surgical patient requiring negative pressure wound therapy.",
      supplierContact: "KCI Medical - 1-800-275-4524",
      alternativeOptions: "Rental equipment available while awaiting shipment"
    }
  ];

  const [showNotifications, setShowNotifications] = useState(false);
  const [animatedMetrics, setAnimatedMetrics] = useState({
    providers: 14,
    orders: 340,
    revenue: 250000
  });

  // Baseline period selector
  const [baselinePeriod, setBaselinePeriod] = useState("lastQuarter");
  
  // Get current period metrics based on selected comparison period
  const getCurrentPeriodMetrics = () => {
    switch (baselinePeriod) {
      case "lastMonth":
        return { providers: 16, orders: 385, revenue: 295000 }; // Current month
      case "lastQuarter":
        return { providers: 16, orders: 1150, revenue: 875000 }; // Current quarter (3 months)
      case "lastYear":
        return { providers: 18, orders: 4200, revenue: 3200000 }; // Current year (12 months)
      case "ytd":
        return { providers: 17, orders: 2800, revenue: 2100000 }; // Year-to-date (8 months through June)
      default:
        return { providers: 16, orders: 1150, revenue: 875000 };
    }
  };
  
  // Get baseline date range based on selected period
  const getBaselinePeriod = () => {
    const now = new Date();
    switch (baselinePeriod) {
      case "lastMonth":
        return {
          label: "vs Last Month",
          dateRange: `${new Date(now.getFullYear(), now.getMonth() - 1, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`,
          metrics: { providers: 15, orders: 340, revenue: 275000 } // Previous month baseline
        };
      case "lastQuarter":
        const lastQuarter = Math.floor((now.getMonth()) / 3);
        const quarterNames = ["Q1", "Q2", "Q3", "Q4"];
        const currentYear = now.getFullYear();
        const lastQuarterName = lastQuarter === 0 ? `Q4 ${currentYear - 1}` : `${quarterNames[lastQuarter - 1]} ${currentYear}`;
        return {
          label: "vs Last Quarter",
          dateRange: lastQuarterName,
          metrics: { providers: 14, orders: 980, revenue: 765000 } // Previous quarter baseline
        };
      case "lastYear":
        return {
          label: "vs Last Year",
          dateRange: `${now.getFullYear() - 1}`,
          metrics: { providers: 15, orders: 3800, revenue: 2850000 } // Previous year baseline
        };
      case "ytd":
        return {
          label: "vs YTD Previous Year",
          dateRange: `Jan-${now.toLocaleDateString('en-US', { month: 'short' })} ${now.getFullYear() - 1}`,
          metrics: { providers: 14, orders: 2400, revenue: 1800000 } // Previous YTD baseline
        };
      default:
        return {
          label: "vs Last Quarter",
          dateRange: "Q1 2025",
          metrics: { providers: 13, orders: 980, revenue: 765000 }
        };
    }
  };

  const baselineData = getBaselinePeriod();
  const currentPeriodMetrics = getCurrentPeriodMetrics();

  // Calculate dynamic growth percentages
  const calculateGrowth = (current: number, baseline: number) => {
    const growth = ((current - baseline) / baseline) * 100;
    return {
      percentage: Math.abs(growth).toFixed(1),
      isPositive: growth >= 0,
      icon: growth >= 0 ? TrendingUp : TrendingDown
    };
  };

  const providersGrowth = calculateGrowth(currentPeriodMetrics.providers, baselineData.metrics.providers);
  const ordersGrowth = calculateGrowth(currentPeriodMetrics.orders, baselineData.metrics.orders);
  const revenueGrowth = calculateGrowth(currentPeriodMetrics.revenue, baselineData.metrics.revenue);

  // Update animated metrics when baseline period changes
  useEffect(() => {
      setAnimatedMetrics(currentPeriodMetrics);
  }, [baselinePeriod]);


  const handleMetricClick = (metric: string) => {
    setSelectedMetric(metric);
    toast({
      title: "Analytics Detail",
      description: `Viewing detailed analytics for ${metric}`,
    });
  };

  const handleOrderClick = (orderType: string) => {
    toast({
      title: "Order Management",
      description: `Opening ${orderType} management interface`,
    });
  };

  const handleProductClick = (productName: string) => {
    toast({
      title: "Product Details",
      description: `Opening details for ${productName}`,
    });
  };

  const handleViewOrderDetails = (order: any) => {
    console.log("View order details clicked", order);
    setSelectedOrder(order);
    setShowOrderDetailsDialog(true);
  };

  const handleInsuranceReview = () => {
    setShowInsuranceReviewList(!showInsuranceReviewList);
  };

  const handleInsuranceOrderAction = (orderId: string, action: string, notes?: string) => {
    toast({
      title: `${action} Completed`,
      description: `Order ${orderId} has been ${action.toLowerCase()}${notes ? ': ' + notes : ''}`
    });
    // In a real application, this would make an API call to update the order status
  };

  const handleShippingTrack = () => {
    setShowShippingDelaysList(!showShippingDelaysList);
  };

  const handleShippingAction = (orderId: string, action: string, notes?: string) => {
    toast({
      title: `${action} Completed`,
      description: `Order ${orderId} shipping ${action.toLowerCase()}${notes ? ': ' + notes : ''}`
    });
    // In a real application, this would make an API call to update the shipping status
  };

  const handleInventoryRestock = () => {
    setShowInventoryShortageList(!showInventoryShortageList);
  };

  const handleInventoryAction = (orderId: string, action: string, notes?: string) => {
    toast({
      title: `${action} Completed`,
      description: `Order ${orderId} inventory ${action.toLowerCase()}${notes ? ': ' + notes : ''}`
    });
    // In a real application, this would make an API call to update the inventory status
  };

  const handleTrackOrder = (order: any) => {
    // Generate tracking information based on order status
    const trackingInfo = {
      ...order,
      trackingNumber: `MTG${order.id.toString().padStart(6, '0')}`,
      estimatedDelivery: new Date(Date.now() + (order.status === 'pending' ? 14 : order.status === 'processing' ? 10 : order.status === 'shipped' ? 3 : 0) * 24 * 60 * 60 * 1000),
      carrier: order.status === 'shipped' || order.status === 'delivered' ? 'FedEx Express' : 'TBD',
      trackingSteps: getTrackingSteps(order.status),
      lastUpdate: new Date(Date.now() - Math.random() * 24 * 60 * 60 * 1000)
    };
    setSelectedTrackingOrder(trackingInfo);
    setShowTrackOrderDialog(true);
  };

  const getTrackingSteps = (status: string) => {
    const baseSteps = [
      { 
        name: 'Order Placed', 
        completed: true, 
        date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
        description: 'Order received and confirmed'
      },
      { 
        name: 'Processing', 
        completed: status !== 'pending', 
        date: status !== 'pending' ? new Date(Date.now() - 5 * 24 * 60 * 60 * 1000) : null,
        description: 'Items being prepared for shipment'
      },
      { 
        name: 'Shipped', 
        completed: status === 'shipped' || status === 'delivered', 
        date: (status === 'shipped' || status === 'delivered') ? new Date(Date.now() - 2 * 24 * 60 * 60 * 1000) : null,
        description: 'Package dispatched to carrier'
      },
      { 
        name: 'Out for Delivery', 
        completed: status === 'delivered', 
        date: status === 'delivered' ? new Date(Date.now() - 4 * 60 * 60 * 1000) : null,
        description: 'Package out for final delivery'
      },
      { 
        name: 'Delivered', 
        completed: status === 'delivered', 
        date: status === 'delivered' ? new Date() : null,
        description: 'Package successfully delivered'
      }
    ];
    return baseSteps;
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat().format(num);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (date: string | Date) => {
    if (!date) return 'N/A';
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return 'N/A';
    return dateObj.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  };




  // Helper functions for Practice and Provider Details Table
  const toggleColumn = (columnKey: keyof typeof visibleColumns) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setFilters({});
    setActiveProviderWidget("");
  };

  const handleProviderWidgetClick = (widgetType: string) => {
    if (activeProviderWidget === widgetType) {
      setActiveProviderWidget("");
    } else {
      setActiveProviderWidget(widgetType);
    }
  };

  // Demo data for practices and providers
  const demoAcmePractice = {
    id: 1,
    name: "Acme Medical Center",
    practiceId: "AMC001",
    address: "123 Healthcare Blvd, Medical City, CA 90210",
    phone: "(555) 123-4567",
    email: "admin@acmemedical.com",
    adminName: "Emily Roberts",
    adminEmail: "emily.roberts@acmemedical.com",
    type: "Multi-Specialty Clinic",
    status: "active",
    agreementType: "Premium Partnership"
  };

  const demoProviders = [
    {
      id: 1,
      name: "Dr. James Smith",
      npi: "1234567890",
      email: "james.smith@acmemedical.com",
      phone: "(555) 123-4571",
      specialties: ["Wound Care", "Dermatology"],
      licenseNumber: "CA12345",
      practiceId: 1,
      status: "active",
      authorizedProducts: ["dressing", "collagen", "hydrogel"]
    },
    {
      id: 2,
      name: "Dr. Sarah Johnson",
      npi: "0987654321",
      email: "sarah.johnson@acmemedical.com",
      phone: "(555) 123-4572",
      specialties: ["Plastic Surgery", "Reconstructive Surgery"],
      licenseNumber: "CA67890",
      practiceId: 1,
      status: "active",
      authorizedProducts: ["dressing", "collagen"]
    },
    {
      id: 3,
      name: "Dr. Michael Rodriguez",
      npi: "1122334455",
      email: "michael.rodriguez@cityhealth.com",
      phone: "(555) 789-0123",
      specialties: ["Emergency Medicine", "Wound Care"],
      licenseNumber: "CA11223",
      practiceId: 2,
      status: "active",
      authorizedProducts: ["dressing", "hydrogel"]
    }
  ];

  const demoPractices = [
    demoAcmePractice,
    {
      id: 2,
      name: "City Health Partners",
      practiceId: "CHP002",
      address: "456 Medical Plaza, Health City, TX 75001",
      phone: "(555) 789-0100",
      email: "admin@cityhealth.com",
      adminName: "Dr. Lisa Thompson",
      adminEmail: "lisa.thompson@cityhealth.com",
      type: "Emergency Medicine",
      status: "active",
      agreementType: "Standard Agreement"
    }
  ];

  // Combine practice and provider data
  const combinedData = useMemo(() => {
    let data: any[] = [];

    // Use demo data
    data = demoProviders.map(provider => {
      const practice = demoPractices.find(p => p.id === provider.practiceId) || demoAcmePractice;
      return {
        practiceId: practice.practiceId,
        practiceName: practice.name,
        providerId: provider.id,
        providerName: provider.name,
        npi: provider.npi,
        specialty: provider.specialties.join(', '),
        city: practice.address.split(',')[1]?.trim() || "Medical City",
        state: practice.address.split(',')[2]?.trim()?.split(' ')[0] || "CA",
        phone: provider.phone,
        status: provider.status,
        agreementType: practice.agreementType
      };
    });

    // Apply widget filter first
    if (activeProviderWidget) {
      switch (activeProviderWidget) {
        case "totalProviders":
          // Show all providers
          break;
        case "activeProviders":
          data = data.filter(record => record.status === "active");
          break;
        case "activePractices":
          const activePracticeIds = [...new Set(data.filter(p => p.status === "active").map(p => p.practiceId))];
          data = data.filter(record => activePracticeIds.includes(record.practiceId));
          break;
        case "premiumAgreements":
          data = data.filter(record => record.agreementType === "Premium Partnership");
          break;
      }
    }

    // Apply search filter
    if (searchTerm) {
      data = data.filter(record => 
        Object.values(record).some(value => 
          value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    // Apply sorting
    if (sortField) {
      data.sort((a, b) => {
        const aValue = a[sortField] || '';
        const bValue = b[sortField] || '';
        const comparison = aValue.toString().localeCompare(bValue.toString());
        return sortDirection === 'asc' ? comparison : -comparison;
      });
    }

    return data;
  }, [searchTerm, sortField, sortDirection, activeProviderWidget]);

  // Statistics based on original unfiltered data
  const originalData = useMemo(() => {
    return demoProviders.map(provider => {
      const practice = demoPractices.find(p => p.id === provider.practiceId) || demoAcmePractice;
      return {
        practiceId: practice.practiceId,
        practiceName: practice.name,
        providerId: provider.id,
        providerName: provider.name,
        npi: provider.npi,
        specialty: provider.specialties.join(', '),
        city: practice.address.split(',')[1]?.trim() || "Medical City",
        state: practice.address.split(',')[2]?.trim()?.split(' ')[0] || "CA",
        phone: provider.phone,
        status: provider.status,
        agreementType: practice.agreementType
      };
    });
  }, []);

  const totalProviders = originalData.length;
  const activeProviders = originalData.filter(p => p.status === "active").length;
  const activePractices = [...new Set(originalData.filter(p => p.status === "active").map(p => p.practiceId))].length;
  const premiumAgreements = originalData.filter(p => p.agreementType === "Premium Partnership").length;

  const renderContent = () => {
    switch (activeSection) {
      default:
        // Product data is now fetched from database via state variable productCatalogData

        // Use real inventory data from state

        // Use categories from configuration, with "All Categories" as first option
        const categories = ["All Categories", ...categoryOptions.map(option => option.label)];


        const stockLevels = ["All Levels", "Critical", "Low", "Sufficient", "Over Stock"];

        const getStockLevel = (current: number, min: number, max: number) => {
          const percentage = (current / max) * 100;
          if (percentage < 15) return "Critical";
          if (percentage < 40) return "Low";
          if (percentage > 100) return "Over Stock";
          return "Sufficient";
        };

        const getStockColor = (level: string) => {
          switch (level) {
            case "Critical": return "bg-red-100 text-red-800 border-red-200";
            case "Low": return "bg-orange-100 text-orange-800 border-orange-200";
            case "Sufficient": return "bg-green-100 text-green-800 border-green-200";
            case "Over Stock": return "bg-blue-100 text-blue-800 border-blue-200";
            default: return "bg-gray-100 text-gray-800 border-gray-200";
          }
        };

        const getStockIcon = (level: string) => {
          switch (level) {
            case "Critical": return <AlertTriangle className="h-4 w-4" />;
            case "Low": return <Clock className="h-4 w-4" />;
            case "Sufficient": return <CheckCircle className="h-4 w-4" />;
            case "Over Stock": return <TrendingUp className="h-4 w-4" />;
            default: return <Package className="h-4 w-4" />;
          }
        };





        const handleViewProduct = (product: any) => {
          console.log('Viewing product:', product);
          console.log('Product URL:', product.productUrl);
          
          if (product.productUrl && product.productUrl.trim() !== '') {
            window.open(product.productUrl, '_blank');
          } else {
            toast({ 
              title: "No Product URL", 
              description: "This product doesn't have a URL configured. Please edit the product to add a URL.", 
              variant: "destructive" 
            });
          }
        };


        const handleViewCategoryProducts = (category: string) => {
          setSelectedCategory(category);
          setActiveProductTab("products");
        };



        return (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <h1 className="text-3xl font-bold text-gray-900 font-size-small:text-2xl font-size-medium:text-3xl font-size-large:text-4xl font-size-extra-large:text-5xl">Product Management</h1>
              <div className="flex space-x-3">
                <Button onClick={() => setShowAddProductDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Product
                </Button>
              </div>
            </div>

            {/* Smart Search - Above Tabs */}
            <Card>
              <CardContent className="pt-6">
                <ProductManagementSmartSearch
                  onSearch={(term: string, field: string) => {
                    setSmartSearchTerm(term);
                    setSmartSearchField(field);
                  }}
                  searchFields={[
                    { value: 'all', label: 'All Fields' },
                    { value: 'name', label: 'Product Name' },
                    // { value: 'id', label: 'Product ID' },
                    { value: 'category', label: 'Category' },
                    { value: 'sku', label: 'SKU' },
                    { value: 'manufacturer', label: 'Manufacturer' }
                  ]}
                />
              </CardContent>
            </Card>

            {/* Tabs */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex space-x-1">
                  <Button 
                    variant={activeProductTab === "products" ? "default" : "ghost"} 
                    size="sm"
                    onClick={() => {
                      setActiveProductTab("products");
                      setSmartSearchTerm("");
                      setSmartSearchField("all");
                    }}
                  >
                    Products
                  </Button>
                  <Button 
                    variant={activeProductTab === "inventory" ? "default" : "ghost"} 
                    size="sm"
                    onClick={() => {
                      setActiveProductTab("inventory");
                      setSmartSearchTerm("");
                      setSmartSearchField("all");
                    }}
                  >
                    Inventory
                  </Button>
                  <Button 
                    variant={activeProductTab === "categories" ? "default" : "ghost"} 
                    size="sm"
                    onClick={() => {
                      setActiveProductTab("categories");
                      setSmartSearchTerm("");
                      setSmartSearchField("all");
                    }}
                  >
                    Categories
                  </Button>
                </div>
              </CardHeader>
            </Card>

            {/* Tab Content */}
            {activeProductTab === "products" && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>
                    {selectedCategory ? `${selectedCategory} Products` : "Product Catalog"}
                  </CardTitle>
                  {selectedCategory && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setSelectedCategory("")}
                    >
                      View All Products
                    </Button>
                  )}
                </CardHeader>
                <CardContent>
                  {catalogLoading ? (
                  <div className={getGridClasses()}>
                      {Array.from({ length: 4 }).map((_, idx) => (
                        <Card key={idx} className="animate-pulse">
                          <CardContent className="p-4 h-[85%] flex flex-col">
                            <div className="space-y-3 flex-1 flex flex-col">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                                </div>
                                <div className="h-5 bg-gray-200 rounded w-16"></div>
                              </div>
                              <div className="h-3 bg-gray-200 rounded"></div>
                              <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                              <div className="h-6 bg-gray-200 rounded w-1/3"></div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : catalogError ? (
                    <div className="text-center py-8">
                      <div className="text-red-600 mb-2">⚠️ Error loading product catalog</div>
                      <div className="text-sm text-gray-600 mb-4">{catalogError}</div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          // Retry loading catalog data
                          const fetchCatalog = async () => {
                            try {
                              setCatalogLoading(true);
                              setCatalogError(null);
                              const productsData = await productService.getAllProducts();
                              const transformedData = productsData.products?.map((product: any) => ({
                                id: product._id,
                                name: product.name,
                                category: product.category,
                                sku: product.sku,
                                price: product.formattedUnitPrice || product.unitPrice,
                                description: product.description,
                                status: product.status || "active",
                                image: `https://images.unsplash.com/photo-1576671081837-49000212a370?w=400&h=300&fit=crop&crop=center&auto=format&q=80&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D`,
                                manufacturer: product.manufacturerId?.companyName || "Unknown Manufacturer",
                                dimensions: "Standard Size",
                                material: "Medical Grade Material",
                                indications: ["General medical use"],
                                contraindications: ["None specified"],
                                sterile: true,
                                shelfLife: "2 years",
                                productUrl: product.productUrl || "",
                                _id: product._id,
                                storage: "Store in cool, dry place",
                                certifications: ["FDA Approved"],
                                // Include productType and variants from backend
                                productType: product.productType || "simple",
                                variants: product.variants || [],
                                baseStockLevel: product.baseStockLevel || 0,
                                baseMinThreshold: product.baseMinThreshold || 10,
                                baseMaxCapacity: product.baseMaxCapacity || 100,
                                lastUpdated: product.updatedAt ? new Date(product.updatedAt).toISOString().split('T')[0] : "2024-01-01",
                                unitsSold: product.unitsSold || 0,
                                revenue: product.formattedRevenue || 0,
                                growthRate: product.formattedGrowthRate || 0
                              })) || [];
                              setProductCatalogData(transformedData);
                            } catch (error) {
                              console.error('Error retrying catalog fetch:', error);
                              setCatalogError(error instanceof Error ? error.message : 'Failed to fetch product catalog');
                            } finally {
                              setCatalogLoading(false);
                            }
                          };
                          fetchCatalog();
                        }}
                      >
                        Retry
                      </Button>
                    </div>
                  ) : (
                    <div className={getGridClasses()}>
                      {productCatalogData.length === 0 ? (
                        <div className="col-span-full text-center py-8">
                          <div className="text-gray-500 mb-2">📦 No products found</div>
                          <div className="text-sm text-gray-400 mb-4">Add your first product to get started</div>
                          <Button 
                            onClick={() => setShowAddProductDialog(true)}
                            size="sm"
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Add Product
                          </Button>
                        </div>
                      ) : (
                        productCatalogData
                      .filter(product => {
                        const categoryMatch = !selectedCategory || product.category === selectedCategory;
                        
                        // Smart search filter
                        if (smartSearchTerm) {
                          const term = smartSearchTerm.toLowerCase();
                          if (smartSearchField === 'all') {
                            return categoryMatch && (
                              product.name?.toLowerCase().includes(term) ||
                              product.id?.toString().toLowerCase().includes(term) ||
                              product.category?.toLowerCase().includes(term) ||
                              product.sku?.toLowerCase().includes(term) ||
                              product.manufacturer?.toLowerCase().includes(term) ||
                              product.description?.toLowerCase().includes(term)
                            );
                          } else if (smartSearchField === 'name') {
                            return categoryMatch && product.name?.toLowerCase().includes(term);
                          } else if (smartSearchField === 'id') {
                            return categoryMatch && product.id?.toString().toLowerCase().includes(term);
                          } else if (smartSearchField === 'category') {
                            return categoryMatch && product.category?.toLowerCase().includes(term);
                          } else if (smartSearchField === 'sku') {
                            return categoryMatch && product.sku?.toLowerCase().includes(term);
                          } else if (smartSearchField === 'manufacturer') {
                            return categoryMatch && product.manufacturer?.toLowerCase().includes(term);
                          }
                        }
                        
                        return categoryMatch;
                      })
                      .map((product) => (
                      <Card key={product.id} className={`hover:shadow-md transition-shadow relative product-card-custom ${getCardHeightClasses()}`}>
                        {/* Delete icon in top right */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteProduct(product)}
                          className="absolute top-2 right-2 h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 z-10"
                          title={`Delete ${product.name}`}
                          aria-label={`Delete ${product.name}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                        <CardContent className="p-4 h-[85%] flex flex-col">
                          <div className="space-y-3 flex-1 flex flex-col">
                            {/* Product Name */}
                            <div className="w-full max-w-full pr-12 sm:pr-12 md:pr-12 lg:pr-14 min-w-0">
                              <h3 
                                className="font-semibold text-gray-900 text-sm leading-tight line-clamp-2 overflow-hidden break-words" 
                                title={product.name}
                              >
                                {product.name}
                              </h3>
                            </div>

                            {/* SKU and Category Row */}
                            <div className="sku-category-row">
                              <div className="sku-text">
                                <p 
                                  className="text-xs text-gray-500 text-truncate-2-lines" 
                                  title={`SKU: ${product.sku}`}
                                >
                                  SKU: {product.sku}
                                </p>
                              </div>
                              <div className="category-text">
                                <p 
                                  className="text-xs text-gray-500 text-truncate-2-lines" 
                                  title={`Type: ${product.category}`}
                                >
                                  Type: {product.category}
                                </p>
                              </div>
                            </div>

                            {/* Product Description */}
                            <p 
                              className="text-sm text-gray-600 line-clamp-2 transition-all duration-200 cursor-help" 
                              title={product.description}
                            >
                              {product.description}
                            </p>
                            <div className="flex justify-between items-center">
                              <span className="text-lg font-bold text-green-600">${product.price}</span>
                              <Badge className={product.status === 'active' ? "bg-green-100 text-green-800 rounded-md" : "bg-gray-100 text-gray-800 rounded-md"}>
                                {product.status === 'active' ? 'Active' : 'Inactive'}
                              </Badge>
                            </div>
                            {/* Action Buttons */}
                            <div className="flex space-x-2">
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="flex-1"
                                onClick={() => handleEditProduct(product)}
                              >
                                <Edit className="h-3 w-3 mr-1" />
                                Edit
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="flex-1" 
                                onClick={() => handleViewProduct(product)}
                              >
                                <Eye className="h-3 w-3 mr-1" />
                                View
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                        ))
                      )}
                  </div>
                  )}
                </CardContent>
              </Card>
            )}

            {activeProductTab === "inventory" && (
              <>
                {/* Inventory Statistics */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Package2 className="h-6 w-6 text-blue-600" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-gray-500">Total Items</p>
                          <p className="text-2xl font-bold text-gray-900">{inventoryStats.totalItems}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                          <AlertTriangle className="h-6 w-6 text-red-600" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-gray-500">Urgent Reorders</p>
                          <p className="text-2xl font-bold text-red-600">{inventoryStats.urgentItems}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                          <Clock className="h-6 w-6 text-orange-600" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-gray-500">Low Stock</p>
                          <p className="text-2xl font-bold text-orange-600">{inventoryStats.lowStockItems + inventoryStats.criticalItems}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                          <DollarSign className="h-6 w-6 text-green-600" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-gray-500">Total Value</p>
                          <p className="text-2xl font-bold text-green-600">${inventoryStats.totalValue.toFixed(2)}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Inventory Items */}
                <Card>
                  <CardHeader>
                    <CardTitle>Inventory Items</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {/* Filters */}
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 mb-4">
                      <div className="flex flex-wrap gap-3">
                        <Select value={inventoryFilters.category} onValueChange={(value) => setInventoryFilters({...inventoryFilters, category: value || ""})}>
                          <SelectTrigger className="w-40">
                            <SelectValue placeholder="Category" />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map(category => (
                              <SelectItem key={category} value={category}>{category}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        <Select value={inventoryFilters.stockLevel} onValueChange={(value) => setInventoryFilters({...inventoryFilters, stockLevel: value || ""})}>
                          <SelectTrigger className="w-40">
                            <SelectValue placeholder="Stock Level" />
                          </SelectTrigger>
                          <SelectContent>
                            {stockLevels.map(level => (
                              <SelectItem key={level} value={level}>{level}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>


                        <Button 
                          variant={inventoryFilters.urgentOnly ? "default" : "outline"} 
                          size="sm"
                          onClick={() => setInventoryFilters({...inventoryFilters, urgentOnly: !inventoryFilters.urgentOnly})}
                        >
                          <Zap className="h-4 w-4 mr-2" />
                          Urgent Only
                        </Button>
                      </div>
                    </div>
                    
                    {inventoryLoading ? (
                    <div className={getGridClasses()}>
                        {Array.from({ length: 4 }).map((_, idx) => (
                          <Card key={idx} className="animate-pulse">
                            <CardContent className="p-4 h-[85%] flex flex-col">
                              <div className="space-y-3 flex-1 flex flex-col">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                                  </div>
                                  <div className="h-5 bg-gray-200 rounded w-16"></div>
                                </div>
                                <div className="h-3 bg-gray-200 rounded"></div>
                                <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                                <div className="h-6 bg-gray-200 rounded w-1/3"></div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    ) : inventoryError ? (
                      <div className="text-center py-8">
                        <div className="text-red-600 mb-2">⚠️ Error loading inventory data</div>
                        <div className="text-sm text-gray-600 mb-4">{inventoryError}</div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => loadInventoryData()}
                        >
                          Retry
                        </Button>
                      </div>
                    ) : (
                      <div className={getGridClasses()}>
                        {filteredInventory.length === 0 ? (
                          <div className="col-span-full text-center py-8">
                            <div className="text-gray-500 mb-2">📦 No inventory items found</div>
                            <div className="text-sm text-gray-400 mb-4">Create sample inventory data to get started</div>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={createSampleInventoryData}
                              disabled={inventoryLoading}
                            >
                              <Package className="h-4 w-4 mr-2" />
                              {inventoryLoading ? "Creating..." : "Create Sample Data"}
                            </Button>
                          </div>
                        ) : (
                          filteredInventory.map((item) => {
                            // Calculate overall product status based on total units vs total max capacity
                            const totalUnits = item.totalStock || 0;
                            const totalMaxCapacity = item.variants?.reduce((sum: number, variant: any) => sum + (variant.maxCapacity || 0), 0) || 100;
                            const overallStockLevel = getStockLevel(totalUnits, 0, totalMaxCapacity);
                            const overallStockPercentage = Math.min((totalUnits / totalMaxCapacity) * 100, 100);
                        
                        return (
                          <Card key={item.id} className={`hover:shadow-md transition-shadow ${overallStockLevel === 'Critical' ? 'ring-2 ring-red-200' : ''} ${getCardHeightClasses()}`}>
                            <CardContent className="p-4 h-[85%] flex flex-col">
                              <div className="space-y-3 flex-1 flex flex-col">
                                {/* Header with urgent indicator */}
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <h3 className="font-semibold text-gray-900 text-sm leading-tight">{item.name}</h3>
                                    <p className="text-xs text-gray-500 mt-1">{item.sku}</p>
                                  </div>
                                  {overallStockLevel === 'Critical' && (
                                    <Badge className="bg-red-100 text-red-800 ml-2 rounded-md">
                                      <AlertTriangle className="h-3 w-3 mr-1" />
                                      Urgent
                                    </Badge>
                                  )}
                                </div>

                                {/* Product Type and Category */}
                                <div className="space-y-1">
                                  <p 
                                    className="text-xs text-gray-500 text-truncate-2-lines" 
                                    title={`Type: ${item.productType === "variant" ? "Multi-Variant" : "Simple"}`}
                                  >
                                    Type: {item.productType === "variant" ? "Multi-Variant" : "Simple"}
                                  </p>
                                  <p 
                                    className="text-xs text-gray-500 text-truncate-2-lines" 
                                    title={`Category: ${item.category}`}
                                  >
                                    Category: {item.category}
                                  </p>
                                </div>

                                {/* Overall Status */}
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs text-gray-600">Overall Status</span>
                                    <Badge className={`text-xs ${
                                      overallStockLevel === 'Over Stock' ? 'bg-blue-100 text-blue-800 border-blue-200' :
                                      overallStockLevel === 'Sufficient' ? 'bg-green-100 text-green-800 border-green-200' :
                                      overallStockLevel === 'Low' ? 'bg-orange-100 text-orange-800 border-orange-200' :
                                      'bg-red-100 text-red-800 border-red-200'
                                    }`}>
                                      <span className="capitalize">{overallStockLevel}</span>
                                    </Badge>
                                  </div>
                                  
                                  {/* Overall Progress Bar */}
                                  <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div 
                                      className={`h-2 rounded-full transition-all ${
                                        overallStockLevel === 'Critical' ? 'bg-red-500' :
                                        overallStockLevel === 'Low' ? 'bg-orange-500' :
                                        overallStockLevel === 'Over Stock' ? 'bg-blue-500' : 'bg-green-500'
                                      }`}
                                      style={{ width: `${overallStockPercentage}%` }}
                                    ></div>
                                  </div>
                                </div>

                                {/* Last Restocked */}
                                <div className="text-xs text-gray-600">
                                  <div className="flex justify-between">
                                    <span>Last Restocked:</span>
                                    <span>{new Date(item.lastRestocked).toLocaleDateString()}</span>
                                  </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex gap-1 mt-auto">
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    className="flex-1 text-xs px-2 py-1 min-w-0"
                                    onClick={() => {
                                      setSelectedInventoryItem(item);
                                      setShowInventoryDetailsModal(true);
                                    }}
                                  >
                                    <Eye className="h-3 w-3 flex-shrink-0" />
                                    <span className="truncate">Details</span>
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    className="flex-1 text-xs px-2 py-1 min-w-0"
                                    onClick={() => {
                                      console.log('Selected item for stock editing:', item);
                                      setSelectedProductForStock(item);
                                      setSelectedProductInventory({
                                        currentStock: item.totalStock || 0,
                                        minStock: 10,
                                        maxStock: item.variants?.reduce((sum: number, variant: any) => sum + (variant.maxCapacity || 0), 0) || 100,
                                        variants: item.variants || []
                                      });
                                      setShowEditStockModal(true);
                                    }}
                                  >
                                    <Edit className="h-3 w-3 mr-1 flex-shrink-0" />
                                    <span className="truncate">Edit Stock</span>
                                  </Button>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })
                        )}
                    </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}

            {activeProductTab === "categories" && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Product Categories</CardTitle>
                  <Button 
                    onClick={() => setShowAddCategoryDialog(true)}
                    // className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Category
                  </Button>
                </CardHeader>
                <CardContent>
                  {categoriesLoading ? (
                    <div className="space-y-3">
                      {[1, 2, 3].map((i) => (
                        <Skeleton key={i} className="h-20 w-full" />
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {categoryOptions && categoryOptions.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {categoryOptions
                          .filter(category => {
                            // Smart search filter for categories
                            if (smartSearchTerm) {
                              const term = smartSearchTerm.toLowerCase();
                              if (smartSearchField === 'all') {
                                return (
                                  category.label?.toLowerCase().includes(term) ||
                                  category.value?.toLowerCase().includes(term)
                                );
                              } else if (smartSearchField === 'name' || smartSearchField === 'category') {
                                return category.label?.toLowerCase().includes(term);
                              } else if (smartSearchField === 'id') {
                                return category.value?.toLowerCase().includes(term);
                              }
                            }
                            return true;
                          })
                          .map((category) => (
                            <Card key={category.value} className="hover:shadow-md transition-shadow relative">
                        <CardContent className="p-4">
                                {/* Delete button in top right */}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteCategory(category)}
                                  className="absolute top-2 right-2 h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                                
                                {/* Category info */}
                                <div className="flex items-center space-x-3 mb-4 pr-8">
                                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                                    <Package className="h-6 w-6 text-blue-600" />
                            </div>
                                  <div className="flex-1 min-w-0">
                                    <h3 
                                      className="font-semibold text-gray-900 truncate" 
                                      title={category.label}
                                    >
                                      {truncateText(category.label, getResponsiveMaxLength(25))}
                                    </h3>
                                    <p className="text-sm text-gray-600">
                                      {productCatalogData.filter(p => p.category === category.label).length} products
                                    </p>
                                  </div>
                                </div>
                                
                                {/* Action buttons at bottom */}
                                <div className="flex space-x-2">
                            <Button 
                              variant="outline" 
                                    size="sm"
                                    onClick={() => handleViewCategoryProducts(category.label)}
                                    className="flex-1"
                            >
                                    <Eye className="h-4 w-4 mr-2" />
                              View Products
                            </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleEditCategory(category)}
                                    className="flex-1"
                                  >
                                    <Edit className="h-4 w-4 mr-2" />
                                    Edit
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                      ) : (
                        <div className="text-center py-12">
                          <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                          <h3 className="text-lg font-medium text-gray-900 mb-2">No categories found</h3>
                          <p className="text-gray-600 mb-4">Get started by creating your first product category.</p>
                          <Button 
                            onClick={() => setShowAddCategoryDialog(true)}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Add Category
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* View Product Dialog */}
            <Dialog open={showViewProductDialog} onOpenChange={setShowViewProductDialog}>
              <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto w-full mx-4">
                <DialogHeader>
                  <DialogTitle>Product Details</DialogTitle>
                  <p className="text-sm text-gray-600">Complete product information and specifications</p>
                </DialogHeader>
                
                {selectedProduct && (
                  <div className="grid gap-6 py-4">
                    {/* Product Image and Basic Info */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <img 
                          src={selectedProduct.image} 
                          alt={selectedProduct.name}
                          className="w-full h-64 object-cover rounded-lg border"
                          onError={(e) => {
                            e.currentTarget.src = "https://via.placeholder.com/400x300/e5e7eb/9ca3af?text=Product+Image";
                          }}
                        />
                        <div className="flex items-center justify-between gap-2">
                          <Badge className="bg-green-100 text-green-800">
                            {selectedProduct.status}
                          </Badge>
                          <Badge 
                            variant="outline"
                            className="max-w-[200px]"
                            title={selectedProduct.category}
                          >
                            <span className="truncate">
                              {truncateText(selectedProduct.category, getResponsiveMaxLength(25))}
                            </span>
                          </Badge>
                        </div>
                      </div>
                      
                      <div className="space-y-4">
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900">{selectedProduct.name}</h3>
                          <p className="text-lg text-gray-600 mt-1">SKU: {selectedProduct.sku}</p>
                          <p className="text-3xl font-bold text-green-600 mt-2">${selectedProduct.price}</p>
                        </div>
                        
                        <div className="space-y-3">
                          <div>
                            <h4 className="font-semibold text-gray-900">Description</h4>
                            <p className="text-gray-600">{selectedProduct.description}</p>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="font-medium text-gray-900">Manufacturer:</span>
                              <p className="text-gray-600">{selectedProduct.manufacturer}</p>
                            </div>
                            <div>
                              <span className="font-medium text-gray-900">Dimensions:</span>
                              <p className="text-gray-600">{selectedProduct.dimensions}</p>
                            </div>
                            <div>
                              <span className="font-medium text-gray-900">Material:</span>
                              <p className="text-gray-600">{selectedProduct.material}</p>
                            </div>
                            <div>
                              <span className="font-medium text-gray-900">Shelf Life:</span>
                              <p className="text-gray-600">{selectedProduct.shelfLife}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Detailed Specifications */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg">Clinical Information</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div>
                            <h5 className="font-semibold text-gray-900 mb-2">Indications</h5>
                            <ul className="space-y-1">
                              {(selectedProduct.indications || []).map((indication: string, idx: number) => (
                                <li key={idx} className="text-sm text-gray-600 flex items-center">
                                  <CheckCircle className="h-3 w-3 text-green-500 mr-2" />
                                  {indication}
                                </li>
                              ))}
                            </ul>
                          </div>
                          
                          <div>
                            <h5 className="font-semibold text-gray-900 mb-2">Contraindications</h5>
                            <ul className="space-y-1">
                              {(selectedProduct.contraindications || []).map((contraindication: string, idx: number) => (
                                <li key={idx} className="text-sm text-gray-600 flex items-center">
                                  <AlertCircle className="h-3 w-3 text-red-500 mr-2" />
                                  {contraindication}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg">Storage & Compliance</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-gray-900">Sterile:</span>
                              <Badge className={selectedProduct.sterile ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}>
                                {selectedProduct.sterile ? "Yes" : "No"}
                              </Badge>
                            </div>
                            
                            <div>
                              <span className="text-sm font-medium text-gray-900">Storage:</span>
                              <p className="text-sm text-gray-600">{selectedProduct.storage}</p>
                            </div>
                          </div>
                          
                          <div>
                            <h5 className="font-semibold text-gray-900 mb-2">Certifications</h5>
                            <div className="flex flex-wrap gap-2">
                              {(selectedProduct.certifications || []).map((cert: string, idx: number) => (
                                <Badge key={idx} variant="outline" className="text-xs">
                                  {cert}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          
                          <div className="text-xs text-gray-500">
                            Last Updated: {new Date(selectedProduct.lastUpdated).toLocaleDateString()}
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-between items-center pt-4 border-t">
                      <div className="flex space-x-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            setShowViewProductDialog(false);
                            handleEditProduct(selectedProduct);
                          }}
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Edit Product
                        </Button>
                        <Button variant="outline" size="sm">
                          <Download className="h-4 w-4 mr-2" />
                          Download Spec Sheet
                        </Button>
                      </div>
                      <Button onClick={() => setShowViewProductDialog(false)}>
                        Close
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>

            {/* Add Product Dialog */}
            <Dialog open={showAddProductDialog} onOpenChange={handleAddProductDialogOpenChange}>
              <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto w-full mx-4">
                <DialogHeader>
                  <DialogTitle>Add New Product</DialogTitle>
                  <p className="text-sm text-gray-600">Create a new product in your catalog</p>
                </DialogHeader>
                <div className="grid gap-2 py-2">
                  <div className="space-y-1">
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                      Product Name
                    </label>
                    <input
                      id="name"
                      value={newProduct.name}
                      onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter product name"
                    />
                  </div>
                  <div className="space-y-1">
                    <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                      Category
                    </label>
                      <ConfigurableSelect
                        category="product_categories"
                        placeholder="Select category"
                        value={newProduct.category}
                        onValueChange={(value) => setNewProduct({...newProduct, category: value})}
                      />
                    </div>
                  <div className="space-y-1">
                    <label htmlFor="sku" className="block text-sm font-medium text-gray-700">
                      SKU
                    </label>
                    <input
                      id="sku"
                      value={newProduct.sku}
                      onChange={(e) => setNewProduct({...newProduct, sku: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter SKU"
                    />
                  </div>
                  {/* Price field - Show for simple products */}
                  {newProduct.productType === "simple" && (
                    <div className="space-y-1">
                      <label htmlFor="price" className="block text-sm font-medium text-gray-700">
                      Price ($)
                    </label>
                    <input
                      id="price"
                      type="number"
                      step="0.01"
                      value={newProduct.price}
                      onChange={(e) => setNewProduct({...newProduct, price: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter price"
                    />
                  </div>
                  )}
                  
                  {/* Base Stock Management - Show for simple products */}
                  {newProduct.productType === "simple" && (
                    <div className="space-y-4">
                      <h4 className="text-lg font-medium text-gray-900">Base Product Stock</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="space-y-1">
                          <label htmlFor="baseStockLevel" className="block text-sm font-medium text-gray-700">
                            Current Stock
                          </label>
                          <input
                            id="baseStockLevel"
                            type="number"
                            min="0"
                            value={newProduct.baseStockLevel || 0}
                            onChange={(e) => setNewProduct({...newProduct, baseStockLevel: parseInt(e.target.value) || 0})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="0"
                          />
                        </div>
                        
                        <div className="space-y-1">
                          <label htmlFor="baseMinThreshold" className="block text-sm font-medium text-gray-700">
                            Min Threshold
                          </label>
                          <input
                            id="baseMinThreshold"
                            type="number"
                            min="0"
                            value={newProduct.baseMinThreshold || 10}
                            onChange={(e) => setNewProduct({...newProduct, baseMinThreshold: parseInt(e.target.value) || 10})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="10"
                          />
                        </div>
                        
                        <div className="space-y-1">
                          <label htmlFor="baseMaxCapacity" className="block text-sm font-medium text-gray-700">
                            Max Capacity
                          </label>
                          <input
                            id="baseMaxCapacity"
                            type="number"
                            min="0"
                            value={newProduct.baseMaxCapacity || 100}
                            onChange={(e) => setNewProduct({...newProduct, baseMaxCapacity: parseInt(e.target.value) || 100})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="100"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-1">
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                      Description
                    </label>
                    <textarea
                      id="description"
                      value={newProduct.description}
                      onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter product description"
                      rows={3}
                    />
                  </div>
                  
                  {/* Product URL */}
                  <div className="space-y-1">
                    <label htmlFor="productUrl" className="block text-sm font-medium text-gray-700">
                      Product URL
                    </label>
                    <input
                      id="productUrl"
                      value={newProduct.productUrl}
                      onChange={(e) => setNewProduct({...newProduct, productUrl: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter product URL (e.g., https://example.com/product)"
                    />
                  </div>

                  
                  {/* Product Type */}
                  <div className="space-y-1">
                    <label htmlFor="productType" className="block text-sm font-medium text-gray-700">
                      Product Type
                    </label>
                    <select
                      id="productType"
                      value={newProduct.productType}
                      onChange={(e) => setNewProduct({ 
                        ...newProduct, 
                        productType: e.target.value as "simple" | "variant", // hybrid support temporarily disabled
                        // Clear variants when switching to simple, clear price when switching to variant
                        variants: e.target.value === "simple" ? [] : newProduct.variants,
                        price: e.target.value === "variant" ? "" : newProduct.price
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="simple">Simple Product (Single Price)</option>
                      <option value="variant">Variant Product (Multiple Sizes/Prices)</option>
                      {/* <option value="hybrid">Hybrid Product (Base Price + Variants)</option> */}
                    </select>
                    <p className="text-xs text-gray-500">
                      {newProduct.productType === "simple" 
                        ? "Product has a single price and size. No variants needed."
                        : newProduct.productType === "variant"
                        ? "Product has multiple sizes/variants, each with its own price."
                        : "Product has a base price and also offers multiple sizes/variants."
                      }
                    </p>
                  </div>
                  
                  {/* Status */}
                  <div className="space-y-2">
                    <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                      Status
                    </label>
                      <select
                        id="status"
                        value={newProduct.status}
                        onChange={(e) => setNewProduct({ ...newProduct, status: e.target.value as "active" | "inactive" })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>

                  {/* Product Variants - Show for variant products */}
                  {newProduct.productType === "variant" && (
                    <div className="col-span-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-lg font-medium text-gray-900">Product Variants</h4>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => addVariant(newProduct, setNewProduct)}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Variant
                      </Button>
                  </div>
                    
                    {newProduct.variants.length === 0 ? (
                      <div className="text-center py-6 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
                        <Package className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                        <p>No variants added yet</p>
                        <p className="text-sm">Click "Add Variant" to create size-specific variants with individual costs</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {newProduct.variants.map((variant, index) => (
                          <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                            <div className="flex items-center justify-between mb-3">
                              <h5 className="font-medium text-gray-900">Variant {index + 1}</h5>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => removeVariant(newProduct, setNewProduct, index)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Size
                                </label>
                                <input
                                  type="text"
                                  value={variant.size}
                                  onChange={(e) => updateVariant(newProduct, setNewProduct, index, 'size', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="e.g., S, M, L, 10x12"
                                />
                              </div>
                              
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  SKU
                                </label>
                                <input
                                  type="text"
                                  value={variant.sku}
                                  onChange={(e) => updateVariant(newProduct, setNewProduct, index, 'sku', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="e.g., {product-sku}-S"
                                />
                              </div>
                              
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Cost ($)
                                </label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={variant.price}
                                  onChange={(e) => updateVariant(newProduct, setNewProduct, index, 'price', parseFloat(e.target.value) || 0)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="0.00"
                                />
                              </div>
                              
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Initial Stock
                                </label>
                                <input
                                  type="number"
                                  value={variant.stockLevel}
                                  onChange={(e) => updateVariant(newProduct, setNewProduct, index, 'stockLevel', parseInt(e.target.value) || 0)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="0"
                                />
                              </div>
                              
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Min Threshold
                                </label>
                                <input
                                  type="number"
                                  value={variant.minThreshold}
                                  onChange={(e) => updateVariant(newProduct, setNewProduct, index, 'minThreshold', parseInt(e.target.value) || 0)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="10"
                                />
                              </div>
                              
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Max Capacity
                                </label>
                                <input
                                  type="number"
                                  value={variant.maxCapacity}
                                  onChange={(e) => updateVariant(newProduct, setNewProduct, index, 'maxCapacity', parseInt(e.target.value) || 0)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="100"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    </div>
                  )}
                </div>
                <div className="flex justify-end space-x-2">
                  <Button variant="outline" onClick={() => handleAddProductDialogOpenChange(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleCreateProduct}
                    disabled={isCreatingProduct}
                  >
                    {isCreatingProduct ? "Adding..." : "Add Product"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Edit Product Dialog */}
            <Dialog open={showEditProductDialog} onOpenChange={setShowEditProductDialog}>
              <DialogContent 
                className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto w-full mx-4"
                onOpenAutoFocus={(e) => e.preventDefault()}
              >
                <DialogHeader>
                  <DialogTitle>Edit Product</DialogTitle>
                  <p className="text-sm text-gray-600">Update product information</p>
                </DialogHeader>
                <div className={editedProduct.productType === "variant" ? "space-y-4 py-2" : "grid gap-2 py-2"}>
                  {/* Row 1: Product Name, Category, SKU - Only for variant products */}
                  {editedProduct.productType === "variant" ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-1">
                        <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700">
                          Product Name
                        </label>
                        <input
                          id="edit-name"
                          value={editedProduct.name}
                          onChange={(e) => setEditedProduct({...editedProduct, name: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter product name"
                        />
                      </div>
                      <div className="space-y-1">
                        <label htmlFor="edit-category" className="block text-sm font-medium text-gray-700">
                          Category
                        </label>
                        <ConfigurableSelect
                          category="product_categories"
                          placeholder="Select category"
                          value={editedProduct.category}
                          onValueChange={(value) => setEditedProduct({...editedProduct, category: value})}
                        />
                      </div>
                      <div className="space-y-1">
                        <label htmlFor="edit-sku" className="block text-sm font-medium text-gray-700">
                          SKU
                        </label>
                        <input
                          id="edit-sku"
                          value={editedProduct.sku}
                          onChange={(e) => setEditedProduct({...editedProduct, sku: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter SKU"
                        />
                      </div>
                    </div>
                  ) : (
                    /* Original layout for simple products */
                    <>
                      <div className="space-y-1">
                        <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700">
                          Product Name
                        </label>
                        <input
                          id="edit-name"
                          value={editedProduct.name}
                          onChange={(e) => setEditedProduct({...editedProduct, name: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter product name"
                        />
                      </div>
                      <div className="space-y-1">
                        <label htmlFor="edit-category" className="block text-sm font-medium text-gray-700">
                          Category
                        </label>
                        <ConfigurableSelect
                          category="product_categories"
                          placeholder="Select category"
                          value={editedProduct.category}
                          onValueChange={(value) => setEditedProduct({...editedProduct, category: value})}
                        />
                      </div>
                      <div className="space-y-1">
                        <label htmlFor="edit-sku" className="block text-sm font-medium text-gray-700">
                          SKU
                        </label>
                        <input
                          id="edit-sku"
                          value={editedProduct.sku}
                          onChange={(e) => setEditedProduct({...editedProduct, sku: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter SKU"
                        />
                      </div>
                    </>
                  )}
                  {/* Price field - Show for simple products */}
                  {editedProduct.productType === "simple" && (
                    <div className="space-y-1">
                      <label htmlFor="edit-price" className="block text-sm font-medium text-gray-700">
                      Price ($)
                    </label>
                    <input
                      id="edit-price"
                      type="number"
                      step="0.01"
                      value={editedProduct.price}
                      onChange={(e) => setEditedProduct({...editedProduct, price: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter price"
                    />
                  </div>
                  )}
                  
                  {/* Base Stock Management - Show for simple products */}
                  {editedProduct.productType === "simple" && (
                    <div className="space-y-4">
                      <h4 className="text-lg font-medium text-gray-900">Base Product Stock</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="space-y-1">
                          <label htmlFor="edit-baseStockLevel" className="block text-sm font-medium text-gray-700">
                            Current Stock
                          </label>
                          <input
                            id="edit-baseStockLevel"
                            type="number"
                            min="0"
                            value={editedProduct.baseStockLevel || 0}
                            onChange={(e) => setEditedProduct({...editedProduct, baseStockLevel: parseInt(e.target.value) || 0})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="0"
                          />
                        </div>
                        
                        <div className="space-y-1">
                          <label htmlFor="edit-baseMinThreshold" className="block text-sm font-medium text-gray-700">
                            Min Threshold
                          </label>
                          <input
                            id="edit-baseMinThreshold"
                            type="number"
                            min="0"
                            value={editedProduct.baseMinThreshold || 10}
                            onChange={(e) => setEditedProduct({...editedProduct, baseMinThreshold: parseInt(e.target.value) || 10})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="10"
                          />
                        </div>
                        
                        <div className="space-y-1">
                          <label htmlFor="edit-baseMaxCapacity" className="block text-sm font-medium text-gray-700">
                            Max Capacity
                          </label>
                          <input
                            id="edit-baseMaxCapacity"
                            type="number"
                            min="0"
                            value={editedProduct.baseMaxCapacity || 100}
                            onChange={(e) => setEditedProduct({...editedProduct, baseMaxCapacity: parseInt(e.target.value) || 100})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="100"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Row 2: Product Description - Separate row for variant products */}
                  {editedProduct.productType === "variant" ? (
                    <div className="space-y-1">
                      <label htmlFor="edit-description" className="block text-sm font-medium text-gray-700">
                        Description
                      </label>
                      <textarea
                        id="edit-description"
                        value={editedProduct.description}
                        onChange={(e) => setEditedProduct({...editedProduct, description: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter product description"
                        rows={3}
                      />
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <label htmlFor="edit-description" className="block text-sm font-medium text-gray-700">
                        Description
                      </label>
                      <textarea
                        id="edit-description"
                        value={editedProduct.description}
                        onChange={(e) => setEditedProduct({...editedProduct, description: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter product description"
                        rows={3}
                      />
                    </div>
                  )}
                  
                  {/* Row 3: URL, Product Type, Status - Only for variant products */}
                  {editedProduct.productType === "variant" ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Product URL */}
                      <div className="space-y-1">
                        <label htmlFor="edit-productUrl" className="block text-sm font-medium text-gray-700">
                          Product URL
                        </label>
                        <input
                          id="edit-productUrl"
                          value={editedProduct.productUrl}
                          onChange={(e) => setEditedProduct({...editedProduct, productUrl: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter product URL"
                        />
                      </div>
                      
                      {/* Product Type */}
                      <div className="space-y-1">
                        <label htmlFor="edit-productType" className="block text-sm font-medium text-gray-700">
                          Product Type
                        </label>
                        <select
                          id="edit-productType"
                          value={editedProduct.productType}
                          onChange={(e) => {
                            setEditedProduct({ 
                              ...editedProduct, 
                              productType: e.target.value as "simple" | "variant",
                              variants: e.target.value === "simple" ? [] : editedProduct.variants,
                              price: e.target.value === "variant" ? "" : editedProduct.price
                            });
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="simple">Simple Product</option>
                          <option value="variant">Variant Product</option>
                        </select>
                      </div>
                      
                      {/* Status */}
                      <div className="space-y-1">
                        <label htmlFor="edit-status" className="block text-sm font-medium text-gray-700">
                          Status
                        </label>
                        <select
                          id="edit-status"
                          value={editedProduct.status}
                          onChange={(e) => setEditedProduct({ ...editedProduct, status: e.target.value as "active" | "inactive" })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>
                    </div>
                  ) : (
                    /* Original layout for simple products */
                    <>
                      {/* Product URL */}
                      <div className="space-y-1">
                        <label htmlFor="edit-productUrl" className="block text-sm font-medium text-gray-700">
                          Product URL
                        </label>
                        <input
                          id="edit-productUrl"
                          value={editedProduct.productUrl}
                          onChange={(e) => setEditedProduct({...editedProduct, productUrl: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter product URL (e.g., https://example.com/product)"
                        />
                      </div>

                      {/* Product Type */}
                      <div className="space-y-1">
                        <label htmlFor="edit-productType" className="block text-sm font-medium text-gray-700">
                          Product Type
                        </label>
                        <select
                          id="edit-productType"
                          value={editedProduct.productType}
                          onChange={(e) => {
                            setEditedProduct({ 
                              ...editedProduct, 
                              productType: e.target.value as "simple" | "variant",
                              variants: e.target.value === "simple" ? [] : editedProduct.variants,
                              price: e.target.value === "variant" ? "" : editedProduct.price
                            });
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="simple">Simple Product (Single Price)</option>
                          <option value="variant">Variant Product (Multiple Sizes/Prices)</option>
                        </select>
                        <p className="text-xs text-gray-500">
                          {editedProduct.productType === "simple" 
                            ? "Product has a single price and size. No variants needed."
                            : "Product has multiple sizes/variants, each with its own price."
                          }
                        </p>
                      </div>
                                        
                      {/* Status */}
                      <div className="space-y-2">
                        <label htmlFor="edit-status" className="block text-sm font-medium text-gray-700">
                          Status
                        </label>
                        <select
                          id="edit-status"
                          value={editedProduct.status}
                          onChange={(e) => setEditedProduct({ ...editedProduct, status: e.target.value as "active" | "inactive" })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>
                    </>
                  )}

                                    {/* Product Variants - Show for variant products */}
                                    {editedProduct.productType === "variant" && (
                                      <div className="col-span-4">
                                      <div className="flex items-center justify-between mb-3">
                                        <h4 className="text-lg font-medium text-gray-900">Product Variants</h4>
                                        <Button
                                          type="button"
                                          variant="outline"
                                          size="sm"
                                          onClick={() => addVariant(editedProduct, setEditedProduct)}
                                        >
                                          <Plus className="h-4 w-4 mr-2" />
                                          Add Variant
                                        </Button>
                  </div>
                                      
                                      {editedProduct.variants.length === 0 ? (
                                        <div className="text-center py-6 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
                                          <Package className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                                          <p>No variants added yet</p>
                                          <p className="text-sm">Click "Add Variant" to create size-specific variants with individual costs</p>
                                        </div>
                                      ) : (
                                        <div className="space-y-4">
                                          {editedProduct.variants.map((variant, index) => (
                                            <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                                              <div className="flex items-center justify-between mb-3">
                                                <h5 className="font-medium text-gray-900">Variant {index + 1}</h5>
                                                <Button
                                                  type="button"
                                                  variant="outline"
                                                  size="sm"
                                                  onClick={() => removeVariant(editedProduct, setEditedProduct, index)}
                                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                >
                                                  <Trash2 className="h-4 w-4" />
                                                </Button>
                                              </div>
                                              
                                              {/* Variant Product ID Display */}
                                              {variant.productId && (
                                                <div className="mb-3 p-2 bg-blue-50 rounded border-l-4 border-blue-400">
                                                  <div className="text-sm text-blue-800">
                                                    <strong>Variant Product ID:</strong> {variant.productId}
                                                  </div>
                                                </div>
                                              )}
                                              
                                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <div>
                                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Size
                                                  </label>
                                                  <input
                                                    type="text"
                                                    value={variant.size}
                                                    onChange={(e) => updateVariant(editedProduct, setEditedProduct, index, 'size', e.target.value)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    placeholder="e.g., S, M, L, 10x12"
                                                  />
                                                </div>
                                                
                                                <div>
                                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    SKU
                                                  </label>
                                                  <input
                                                    type="text"
                                                    value={variant.sku}
                                                    onChange={(e) => updateVariant(editedProduct, setEditedProduct, index, 'sku', e.target.value)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    placeholder="e.g., {product-sku}-S"
                                                  />
                                                </div>
                                                
                                                <div>
                                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Cost ($)
                                                  </label>
                                                  <input
                                                    type="number"
                                                    step="0.01"
                                                    value={variant.price}
                                                    onChange={(e) => updateVariant(editedProduct, setEditedProduct, index, 'price', parseFloat(e.target.value) || 0)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    placeholder="0.00"
                                                  />
                                                </div>
                                                
                                                <div>
                                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Current Stock
                                                  </label>
                                                  <input
                                                    type="number"
                                                    value={variant.stockLevel}
                                                    onChange={(e) => updateVariant(editedProduct, setEditedProduct, index, 'stockLevel', parseInt(e.target.value) || 0)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    placeholder="0"
                                                  />
                                                </div>
                                                
                                                <div>
                                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Min Threshold
                                                  </label>
                                                  <input
                                                    type="number"
                                                    value={variant.minThreshold}
                                                    onChange={(e) => updateVariant(editedProduct, setEditedProduct, index, 'minThreshold', parseInt(e.target.value) || 0)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    placeholder="10"
                                                  />
                                                </div>
                                                
                                                <div>
                                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Max Capacity
                                                  </label>
                                                  <input
                                                    type="number"
                                                    value={variant.maxCapacity}
                                                    onChange={(e) => updateVariant(editedProduct, setEditedProduct, index, 'maxCapacity', parseInt(e.target.value) || 0)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    placeholder="100"
                                                  />
                                                </div>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                      </div>
                                    )}
                </div>
                <div className="flex justify-end space-x-2">
                  <Button variant="outline" onClick={() => setShowEditProductDialog(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleUpdateProduct}
                    disabled={isUpdatingProduct}
                  >
                    {isUpdatingProduct ? "Updating..." : "Update Product"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Add Category Dialog */}
            <Dialog open={showAddCategoryDialog} onOpenChange={setShowAddCategoryDialog}>
              <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto w-full mx-4">
                <DialogHeader>
                  <DialogTitle>Add New Category</DialogTitle>
                  <p className="text-sm text-gray-600">Create a new product category for organizing your products</p>
                </DialogHeader>
                <div className="space-y-4 py-4">
                    <div className="space-y-2">
                    <label htmlFor="category-name" className="text-sm font-medium text-gray-700">
                      Category Name *
                    </label>
                    <input
                      id="category-name"
                      type="text"
                      value={newCategory.name}
                      onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                      placeholder="Enter category name"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    </div>
                                  <div className="space-y-2">
                    <label htmlFor="category-description" className="text-sm font-medium text-gray-700">
                      Description
                    </label>
                    <textarea
                      id="category-description"
                      value={newCategory.description}
                      onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                      placeholder="Enter category description (optional)"
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                                        </div>
                                      </div>
                <div className="flex justify-end space-x-2">
                  <Button variant="outline" onClick={() => setShowAddCategoryDialog(false)}>
                    Cancel
                                  </Button>
                                  <Button 
                    onClick={handleCreateCategory}
                    disabled={isCreatingCategory}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {isCreatingCategory ? "Creating..." : "Create Category"}
                                  </Button>
                                </div>
              </DialogContent>
            </Dialog>

            {/* Edit Category Dialog */}
            <Dialog open={showEditCategoryDialog} onOpenChange={setShowEditCategoryDialog}>
              <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto w-full mx-4">
                <DialogHeader>
                  <DialogTitle>Edit Category</DialogTitle>
                  <p className="text-sm text-gray-600">
                    Update the category information
                    {selectedCategoryForEdit && (
                      <span className="block mt-1 text-blue-600 font-medium">
                        {productCatalogData.filter(p => p.category === selectedCategoryForEdit.label).length} products currently use this category
                      </span>
                    )}
                  </p>
                </DialogHeader>
                <div className="space-y-4 py-4">
                                  <div className="space-y-2">
                    <label htmlFor="edit-category-name" className="text-sm font-medium text-gray-700">
                      Category Name *
                    </label>
                    <input
                      id="edit-category-name"
                      type="text"
                      value={editedCategory.name}
                      onChange={(e) => setEditedCategory({ ...editedCategory, name: e.target.value })}
                      placeholder="Enter category name"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                                        </div>
                                <div className="space-y-2">
                    <label htmlFor="edit-category-description" className="text-sm font-medium text-gray-700">
                      Description
                    </label>
                    <textarea
                      id="edit-category-description"
                      value={editedCategory.description}
                      onChange={(e) => setEditedCategory({ ...editedCategory, description: e.target.value })}
                      placeholder="Enter category description (optional)"
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                                </div>
                              </div>
                <div className="flex justify-end space-x-2">
                  <Button variant="outline" onClick={() => setShowEditCategoryDialog(false)}>
                    Cancel
                        </Button>
                                  <Button 
                    onClick={handleUpdateCategory}
                    disabled={isUpdatingCategory}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {isUpdatingCategory ? "Updating..." : "Update Category"}
                                  </Button>
                                </div>
              </DialogContent>
            </Dialog>

                      </div>
        );
    }
  };

  return (
    
    <div className="product-management-page w-full min-h-screen bg-gray-50 flex">
      
      {/* Main Content Area */}
      <div className={`flex-1 transition-all duration-300 w-full`}>
        {/* Dashboard Content */}
        <div className="p-8">
          {renderContent()}
        </div>
      </div>

      {/* Order Details Dialog - Available at component level */}
      <Dialog open={showOrderDetailsDialog} onOpenChange={setShowOrderDetailsDialog}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto w-full mx-4">
          <DialogHeader>
            <DialogTitle>Order Details</DialogTitle>
            <p className="text-sm text-gray-600">Complete order information and items</p>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="grid gap-6 py-4">
              {/* Order Header Information */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Order Information</h3>
                    <div className="mt-2 space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-600">Order #:</span>
                        <span className="text-sm text-gray-900">{selectedOrder.id}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-600">Order Date:</span>
                        <span className="text-sm text-gray-900">{new Date(selectedOrder.date).toLocaleDateString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-600">Order Status:</span>
                        <Badge className={
                          selectedOrder.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          selectedOrder.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                          selectedOrder.status === 'shipped' ? 'bg-purple-100 text-purple-800' :
                          'bg-green-100 text-green-800'
                        }>
                          {selectedOrder.status.charAt(0).toUpperCase() + selectedOrder.status.slice(1)}
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-600">Total Value:</span>
                        <span className="text-sm font-semibold text-green-600">${selectedOrder.value.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Provider Information</h3>
                    <div className="mt-2 space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-600">Provider:</span>
                        <span className="text-sm text-gray-900">{selectedOrder.provider}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-600">Practice Type:</span>
                        <span className="text-sm text-gray-900">{selectedOrder.providerType}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-600">Contact:</span>
                        <span className="text-sm text-gray-900">{selectedOrder.contact}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-600">Phone:</span>
                        <span className="text-sm text-gray-900">{selectedOrder.phone}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Order Items</h3>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Price</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {selectedOrder.items.map((item: any, index: number) => (
                        <tr key={index}>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {item.name}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.quantity}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            ${item.price.toFixed(2)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                            ${(item.quantity * item.price).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Order Summary */}
                <div className="flex justify-end">
                  <div className="w-64 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Subtotal:</span>
                      <span className="text-gray-900">${selectedOrder.value.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Tax:</span>
                      <span className="text-gray-900">${(selectedOrder.value * 0.08).toFixed(2)}</span>
                    </div>
                    <div className="border-t pt-2">
                      <div className="flex justify-between text-base font-semibold">
                        <span className="text-gray-900">Total:</span>
                        <span className="text-gray-900">${(selectedOrder.value * 1.08).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Track Order Dialog */}
      <Dialog open={showTrackOrderDialog} onOpenChange={setShowTrackOrderDialog}>
        <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto w-full mx-4">
          <DialogHeader>
            <DialogTitle>Track Order</DialogTitle>
            <p className="text-sm text-gray-600">Real-time order tracking and delivery information</p>
          </DialogHeader>
          
          {selectedTrackingOrder && (
            <div className="grid gap-6 py-4">
              {/* Tracking Header */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-blue-50 rounded-lg">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-600">Order #:</span>
                    <span className="text-sm font-semibold text-gray-900">{selectedTrackingOrder.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-600">Tracking #:</span>
                    <span className="text-sm font-mono text-blue-600">{selectedTrackingOrder.trackingNumber}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-600">Carrier:</span>
                    <span className="text-sm text-gray-900">{selectedTrackingOrder.carrier}</span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-600">Est. Delivery:</span>
                    <span className="text-sm font-semibold text-green-600">
                      {selectedTrackingOrder.estimatedDelivery.toLocaleDateString('en-US', { 
                        weekday: 'short', 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-600">Last Update:</span>
                    <span className="text-sm text-gray-900">
                      {selectedTrackingOrder.lastUpdate.toLocaleString('en-US', { 
                        month: 'short', 
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-600">Status:</span>
                    <Badge className={
                      selectedTrackingOrder.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      selectedTrackingOrder.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                      selectedTrackingOrder.status === 'shipped' ? 'bg-purple-100 text-purple-800' :
                      'bg-green-100 text-green-800'
                    }>
                      {selectedTrackingOrder.status.charAt(0).toUpperCase() + selectedTrackingOrder.status.slice(1)}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Tracking Timeline */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Shipment Progress</h3>
                <div className="relative">
                  {selectedTrackingOrder.trackingSteps.map((step: any, index: number) => (
                    <div key={index} className="flex items-center mb-6 last:mb-0">
                      {/* Timeline connector */}
                      {index < selectedTrackingOrder.trackingSteps.length - 1 && (
                        <div 
                          className={`absolute left-4 top-8 w-0.5 h-16 ${
                            step.completed ? 'bg-green-400' : 'bg-gray-200'
                          }`}
                        />
                      )}
                      
                      {/* Step indicator */}
                      <div className={`relative z-10 w-8 h-8 rounded-full border-2 flex items-center justify-center ${
                        step.completed 
                          ? 'bg-green-500 border-green-500 text-white' 
                          : 'bg-white border-gray-300 text-gray-400'
                      }`}>
                        {step.completed ? (
                          <CheckCircle className="h-5 w-5" />
                        ) : (
                          <div className="w-2 h-2 bg-gray-300 rounded-full" />
                        )}
                      </div>
                      
                      {/* Step content */}
                      <div className="ml-4 flex-1">
                        <div className="flex items-center justify-between">
                          <h4 className={`text-sm font-medium ${
                            step.completed ? 'text-gray-900' : 'text-gray-500'
                          }`}>
                            {step.name}
                          </h4>
                          {step.date && (
                            <span className="text-xs text-gray-500">
                              {step.date.toLocaleString('en-US', { 
                                month: 'short', 
                                day: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit'
                              })}
                            </span>
                          )}
                        </div>
                        <p className={`text-xs mt-1 ${
                          step.completed ? 'text-gray-600' : 'text-gray-400'
                        }`}>
                          {step.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Shipment Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Shipment Details</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600">Items:</span>
                      <span className="text-sm text-gray-900">{selectedTrackingOrder.items.length} products</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600">Weight:</span>
                      <span className="text-sm text-gray-900">
                        {(selectedTrackingOrder.items.length * 2.5).toFixed(1)} lbs
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600">Package Type:</span>
                      <span className="text-sm text-gray-900">Standard Box</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600">Service Level:</span>
                      <span className="text-sm text-gray-900">
                        {selectedTrackingOrder.status === 'shipped' || selectedTrackingOrder.status === 'delivered' 
                          ? 'Express Delivery' 
                          : 'Standard Shipping'}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Delivery Address</h3>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p className="font-medium text-gray-900">{selectedTrackingOrder.provider}</p>
                    <p>Medical Plaza Building</p>
                    <p>1234 Healthcare Drive</p>
                    <p>Suite 100</p>
                    <p>Medical City, MC 12345</p>
                  </div>
                  
                  {selectedTrackingOrder.status === 'shipped' && (
                    <div className="mt-4 p-3 bg-purple-50 rounded-lg">
                      <div className="flex items-center">
                        <Clock className="h-4 w-4 text-purple-600 mr-2" />
                        <span className="text-sm font-medium text-purple-800">Out for Delivery</span>
                      </div>
                      <p className="text-xs text-purple-700 mt-1">
                        Expected delivery today between 10:00 AM - 6:00 PM
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Order Items Summary */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="text-lg font-semibold text-gray-900">Items in this Shipment</h3>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {selectedTrackingOrder.items.map((item: any, index: number) => (
                        <tr key={index}>
                          <td className="px-4 py-4">
                            <div>
                              <p className="text-sm font-medium text-gray-900">{item.name}</p>
                              <p className="text-xs text-gray-500">SKU: {`${item.name.substring(0, 3).toUpperCase()}-${(index + 1).toString().padStart(3, '0')}`}</p>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-900">{item.quantity}</td>
                          <td className="px-4 py-4">
                            <Badge className="bg-green-100 text-green-800">
                              {selectedTrackingOrder.status === 'delivered' ? 'Delivered' : 'In Transit'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Stock Modal */}
      <EditStockModal
        isOpen={showEditStockModal}
        onClose={() => setShowEditStockModal(false)}
        product={selectedProductForStock}
        inventoryData={selectedProductInventory}
        onSave={handleSaveStock}
        isLoading={isUpdatingStock}
      />

      {/* Inventory Details Modal */}
      <Dialog open={showInventoryDetailsModal} onOpenChange={setShowInventoryDetailsModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">
              Inventory Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedInventoryItem && (
            <div className="space-y-6">
              {/* Header Section */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">{selectedInventoryItem.name}</h2>
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <span><strong>SKU:</strong> {selectedInventoryItem.sku}</span>
                      
                      <div>
                    <span className="text-gray-600">Category:</span>
                    <span className="ml-2 font-medium">{selectedInventoryItem.category}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Type:</span>
                    <span className="ml-2 font-medium">{selectedInventoryItem.productType === "variant" ? "Multi-Variant" : "Simple"}</span>
                  </div>
                  
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-600 mb-1">Overall Status</div>
                    {(() => {
                      const totalUnits = selectedInventoryItem.totalStock || 0;
                      const totalMaxCapacity = selectedInventoryItem.variants?.reduce((sum: number, variant: any) => sum + (variant.maxCapacity || 0), 0) || 100;
                      const overallStockLevel = getStockLevel(totalUnits, 0, totalMaxCapacity);
                      return (
                        <Badge className={`text-sm ${
                          overallStockLevel === 'Over Stock' ? 'bg-blue-100 text-blue-800 border-blue-200' :
                          overallStockLevel === 'Sufficient' ? 'bg-green-100 text-green-800 border-green-200' :
                          overallStockLevel === 'Low' ? 'bg-orange-100 text-orange-800 border-orange-200' :
                          'bg-red-100 text-red-800 border-red-200'
                        }`}>
                          <span className="capitalize">{overallStockLevel}</span>
                        </Badge>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Stock Overview */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-4">
                    <div className="flex items-center">
                      <Package className="h-8 w-8 text-blue-600 mr-3" />
                      <div>
                        <div className="text-sm text-blue-600 font-medium">Total Stock</div>
                        <div className="text-2xl font-bold text-blue-900">{selectedInventoryItem.totalStock || 0}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-4">
                    <div className="flex items-center">
                      <DollarSign className="h-8 w-8 text-green-600 mr-3" />
                      <div>
                        <div className="text-sm text-green-600 font-medium">Unit Price</div>
                        <div className="text-2xl font-bold text-green-900">${selectedInventoryItem.unitPrice?.toFixed(2) || '0.00'}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-orange-50 border-orange-200">
                  <CardContent className="p-4">
                    <div className="flex items-center">
                      <Calendar className="h-8 w-8 text-orange-600 mr-3" />
                      <div>
                        <div className="text-sm text-orange-600 font-medium">Last Restocked</div>
                        <div className="text-lg font-bold text-orange-900">{new Date(selectedInventoryItem.lastRestocked).toLocaleDateString()}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Product Variants */}
              {selectedInventoryItem.productType === "variant" && selectedInventoryItem.variants && selectedInventoryItem.variants.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <Package2 className="h-5 w-5 mr-2" />
                    Product Variants ({selectedInventoryItem.variants.length} sizes)
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedInventoryItem.variants.map((variant: any, index: number) => {
                      const variantStockLevel = getStockLevel(variant.stockLevel, variant.minThreshold, variant.maxCapacity);
                      const variantPercentage = Math.min((variant.stockLevel / variant.maxCapacity) * 100, 100);
                      
                      return (
                        <Card key={index} className="border-l-4 border-l-blue-500">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-3">
                              <div>
                                <h4 className="font-semibold text-gray-900">{variant.size}</h4>
                                <p className="text-sm text-gray-600">${variant.price.toFixed(2)}</p>
                              </div>
                              <Badge className={`text-xs ${
                                variantStockLevel === 'Over Stock' ? 'bg-blue-100 text-blue-800' :
                                variantStockLevel === 'Sufficient' ? 'bg-green-100 text-green-800' :
                                variantStockLevel === 'Low' ? 'bg-orange-100 text-orange-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {variantStockLevel}
                              </Badge>
                            </div>
                            
                            <div className="space-y-2">
                              <div className="flex justify-between text-sm text-gray-600">
                                <span>Stock Level</span>
                                <span className="font-medium">{variant.stockLevel} / {variant.maxCapacity}</span>
                              </div>
                              
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div 
                                  className={`h-2 rounded-full transition-all ${
                                    variantStockLevel === 'Critical' ? 'bg-red-500' :
                                    variantStockLevel === 'Low' ? 'bg-orange-500' :
                                    variantStockLevel === 'Over Stock' ? 'bg-blue-500' : 'bg-green-500'
                                  }`}
                                  style={{ width: `${variantPercentage}%` }}
                                ></div>
                              </div>
                              
                              <div className="flex justify-between text-xs text-gray-500">
                                <span>Min Threshold: {variant.minThreshold}</span>
                                <span className={`font-medium ${
                                  variantStockLevel === 'Critical' ? 'text-red-600' :
                                  variantStockLevel === 'Low' ? 'text-orange-600' :
                                  variantStockLevel === 'Over Stock' ? 'text-blue-600' : 'text-green-600'
                                }`}>
                                  {variantPercentage.toFixed(0)}%
                                </span>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Simple Product Details */}
              {selectedInventoryItem.productType === "simple" && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <Package2 className="h-5 w-5 mr-2" />
                    Product Details
                  </h3>
                  
                  <Card className="border-l-4 border-l-green-500">
                    <CardContent className="p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <div className="text-sm text-gray-600 mb-1">Base Stock Level</div>
                          <div className="text-lg font-semibold text-gray-900">{selectedInventoryItem.baseStockLevel || 0} units</div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600 mb-1">Status</div>
                          <div className={`text-lg font-semibold ${
                            selectedInventoryItem.status === 'Critical' ? 'text-red-600' :
                            selectedInventoryItem.status === 'Low' ? 'text-orange-600' :
                            selectedInventoryItem.status === 'Over Stock' ? 'text-blue-600' : 'text-green-600'
                          }`}>
                            {selectedInventoryItem.baseStockLevel > 0 ? 'In Stock' : 'Out of Stock'}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Additional Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Additional Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Category:</span>
                    <span className="ml-2 font-medium">{selectedInventoryItem.category}</span>
                  </div>
              
                  <div>
                    <span className="text-gray-600">Total Value:</span>
                    <span className="ml-2 font-medium">${selectedInventoryItem.totalValue?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Product ID:</span>
                    <span className="ml-2 font-medium font-mono text-xs">{selectedInventoryItem.id}</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => setShowInventoryDetailsModal(false)}
                >
                  Close
                </Button>
                <Button 
                  onClick={() => {
                    setShowInventoryDetailsModal(false);
                    setSelectedProductForStock(selectedInventoryItem);
                    setSelectedProductInventory({
                      currentStock: selectedInventoryItem.totalStock || 0,
                      minStock: 10,
                      maxStock: selectedInventoryItem.variants?.reduce((sum: number, variant: any) => sum + (variant.maxCapacity || 0), 0) || 100,
                      variants: selectedInventoryItem.variants || []
                    });
                    setShowEditStockModal(true);
                  }}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Stock
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Toaster />
    </div>
    
  );
}